<template>
  <div class="px-4 pb-4">
    <!-- Skeleton loader for initial load -->
    <!-- Redesign layout (SC-45776): 3-col rail/content/summary shadow, gated
         behind the settlementRedesign flag. 'heading, list-item@6' renders the
         rail's title bar plus one row per wizard step via Vuetify's own
         composite-type syntax — no custom markup needed. -->
    <SkeletonRenderer
      v-if="uiState.loading && isSettlementRedesignEnabled"
      :spec="[
        {
          type: 'row',
          spacing: 'gap-6',
          class: 'grid grid-cols-[220px_1fr_280px]',
          columns: [
            { type: 'heading, list-item@6' },
            { type: 'heading, card, paragraph' },
            { type: 'card' },
          ],
        },
      ]"
      container-class="pa-0 mt-2"
    />

    <!-- Old (non-redesign) layout skeleton — unchanged -->
    <SkeletonRenderer
      v-else-if="uiState.loading"
      :spec="[
        { type: 'heading', class: 'mb-4' },
        { type: 'card', class: 'mb-4' },
        {
          type: 'row',
          spacing: 'gap-6',
          class: 'grid grid-cols-[1fr_320px]',
          columns: [{ type: 'card, card' }, { type: 'card' }],
        },
      ]"
      container-class="pa-0"
    />

    <!-- Main content -->
    <v-container v-else fluid class="pa-0">
      <!-- ═══ FULL-PAGE GATES ═══════════════════════════════ -->

      <!-- GATE: Trust Accounting Not Enabled (DD-17) -->
      <SettlementGate
        v-if="!isTrustEnabled"
        icon="🏦"
        title="Enable Trust Accounting to Use Settlements"
        sidebar-label="🏦 Set Up Trust Accounting"
        data-cy="trust-gate"
      >
        <p
          class="text-body-1 text-grey-darken-2 text-center mb-6"
          style="max-width: 520px"
        >
          The Settlement Calculator tracks trust deposits, lien balances, and
          disbursements &mdash; all of which require trust accounting. Enable
          trust accounting in your firm settings to get started.
        </p>
        <LlSecondaryButton
          variant="outlined"
          color="primary"
          data-cy="go-to-trust-settings"
          @click="goToTrustSettings"
        >
          Go to Trust Accounting Settings
        </LlSecondaryButton>
        <p
          class="text-body-2 text-grey-darken-1 text-center mt-6"
          style="max-width: 520px"
        >
          Once trust accounting is enabled, return to this matter to access the
          Settlement Calculator.
        </p>
      </SettlementGate>

      <!-- GATE: Insufficient Permissions (DD-18) -->
      <SettlementGate
        v-else-if="!hasSettlementPermission"
        icon="🔒"
        title="Insufficient Permissions"
        sidebar-label="🔒 Settlement Tab Unavailable"
        data-cy="settlement-permission-gate"
      >
        <div
          class="text-body-1 text-grey-darken-2 text-center mb-6"
          style="max-width: 520px"
        >
          The Settlement tab requires <b>both</b> of the following permissions:
          <ul class="text-left mx-auto mt-4 mb-4" style="max-width: 420px">
            <li>Create/edit access on clients and matters</li>
            <li>Billing tab access (ability to submit invoices to QuickBooks)</li>
          </ul>
          Please contact your firm administrator if you need access.
        </div>
      </SettlementGate>

      <!-- ═══ ACTIVE WIZARD (only when no full-page gate) ═══ -->
      <template v-else>
        <!-- ─── Settings Modal (v-navigation-drawer) ────── -->
        <SettlementSettingsModal
          ref="settingsModalRef"
          :loading="uiState.loading"
          :settings-form="settingsForm"
          :is-locked="settlementStore.isLocked"
          :can-reset="settlementStore.canReset"
          :reset-blocked-reason="settlementStore.resetBlockedReason"
          :company-expense-interest-rate="companyExpenseInterestRate"
          :no-expense-interest="matterNoExpenseInterest"
          :expense-from-date="settlementStore.expenses.expenseFromDate"
          :case-status="matterData?.caseStatus ?? null"
          :case-status-options="caseStatusOptions"
          :redesign-enabled="isSettlementRedesignEnabled"
          :saving="savingSettings"
          @save="onSettingsSave"
        />

        <!-- ─── Settings toolbar row ──────────────────────── -->
        <div class="flex items-center gap-4 mb-3 min-h-[32px]">
          <!-- Status / warning message -->
          <span
            v-if="
              !settlementStore.canReset && settlementStore.resetBlockedReason
            "
            class="text-xs text-error flex items-center gap-1 shrink-0"
          >
            <LlIcon icon="mdi-lock" size="13" color="error" />
            {{ settlementStore.resetBlockedReason }}
          </span>
          <span
            v-else-if="settlementStore.isLocked && settlementStore.canReset"
            class="text-xs text-orange-darken-2 flex items-center gap-1 shrink-0"
          >
            <v-icon size="13" color="orange-darken-2">mdi-lock</v-icon>
            Settings locked &middot; frozen after statement
          </span>
          <span
            v-else-if="
              !settlementStore.settingsLocked &&
              settlementStore.hasCompletedSteps
            "
            class="text-xs text-orange-darken-2 flex items-center gap-1 shrink-0"
          >
            <v-icon size="13" color="orange-darken-2">mdi-alert</v-icon>
            Changing settings will reset all completed steps
          </span>

          <!-- Stats bar -->
          <div class="ml-auto flex items-center">
            <template v-for="(stat, i) in settlementStats" :key="stat.label">
              <span
                v-if="i > 0"
                class="mx-3 text-gray-300 select-none"
                style="font-size: 16px; line-height: 1"
                >·</span
              >
              <LlTooltip location="bottom">
                <template #activator>
                  <span
                    class="stat-chip flex items-center gap-1 text-sm text-gray-700 cursor-pointer select-none"
                    @click="openSettingsAndFocus(stat.focusField)"
                  >
                    <v-icon size="16">{{ stat.icon }}</v-icon>
                    {{ stat.display }}
                  </span>
                </template>
                <div :style="{ maxWidth: stat.tooltipMaxWidth + 'px' }">
                  <div
                    class="text-xs font-bold uppercase mb-1 text-green-regular tracking-wider"
                  >
                    {{ stat.label }}
                  </div>
                  <div
                    v-if="stat.tooltipValue"
                    class="text-xs leading-snug font-medium text-white"
                  >
                    {{ stat.tooltipValue }}
                  </div>
                  <div class="text-xs leading-snug mt-0.5 text-gray-300">
                    {{ stat.description }}
                  </div>
                </div>
              </LlTooltip>
            </template>
          </div>

          <template v-if="settlementStore.hasCompletedSteps && !settlementStore.isLocked">
            <LlTooltip v-if="!settlementStore.canReset" location="bottom">
              <div class="text-center whitespace-pre-line">
                {{ settlementStore.resetBlockedReason || 'Reset is not available' }}
              </div>
              <template #activator>
                <LlSecondaryButton
                  color="error"
                  variant="text"
                  prepend-icon="mdi-lock"
                  size="small"
                  data-cy="settlement-reset-steps-btn"
                  disabled
                >
                  Reset Steps
                </LlSecondaryButton>
              </template>
            </LlTooltip>
            <LlSecondaryButton
              v-else
              color="error"
              variant="text"
              prepend-icon="mdi-refresh"
              size="small"
              data-cy="settlement-reset-steps-btn"
              @click="showUnlockDialog = true"
            >
              Reset Steps
            </LlSecondaryButton>
          </template>

          <!-- Settings button -->
          <LlSecondaryButton
            variant="outlined"
            size="small"
            prepend-icon="mdi-cog"
            data-cy="settlement-settings-btn"
            @click="openSettingsPanel"
          >
            Settings
          </LlSecondaryButton>
        </div>

        <!-- ─── SC-45776: Wizard Layout — 3-col with vertical step rail ─── -->
        <!-- ─────────────────────────────────────────────────────────────────
             SC-45776: Redesign — 3-column grid with vertical sticky rail.
             Gated behind the `settlementRedesign` feature flipper.
             Falls back to the original horizontal v-stepper when off.
             ───────────────────────────────────────────────────────────── -->
        <div v-if="isSettlementRedesignEnabled" class="sett-wizard-grid mt-2">

          <!-- Column 1: Vertical sticky step rail -->
          <div class="sett-rail">
            <div class="sett-rail-head">
              <div class="sett-rail-title">SETTLEMENT STEPS</div>
              <div class="sett-rail-progress">
                <div class="sett-rail-progress-bar" :style="{ width: railProgressPercent + '%' }"></div>
              </div>
              <div class="sett-rail-count">Step {{ displayStepIndex }} of {{ visibleStepCount }}</div>
              <div v-if="settlementStore.isGenerated" class="sett-rail-locked-badge">
                <v-icon size="11" class="mr-1">mdi-lock</v-icon>Statement generated
              </div>
            </div>
            <template v-for="(step, idx) in visibleSteps" :key="step.number">
              <div
                class="sett-step"
                :class="{
                  'sett-step--active': activeStep === step.number,
                  'sett-step--completed': isStepCompleted(step.number),
                  'sett-step--clickable': canNavigateToStep(step.number),
                }"
                :role="canNavigateToStep(step.number) ? 'button' : undefined"
                :tabindex="canNavigateToStep(step.number) ? 0 : undefined"
                :aria-current="activeStep === step.number ? 'step' : undefined"
                :aria-label="`Step ${step.displayNumber}: ${step.label}${isStepCompleted(step.number) ? ' (completed)' : ''}`"
                @click="handleRailStepClick(step.number)"
                @keydown.enter.prevent="handleRailStepClick(step.number)"
                @keydown.space.prevent="handleRailStepClick(step.number)"
              >
                <div class="sett-step-circle">
                  <v-icon v-if="isStepCompleted(step.number)" size="13">mdi-check</v-icon>
                  <span v-else>{{ step.displayNumber }}</span>
                </div>
                <div class="sett-step-label">{{ step.label }}</div>
              </div>
              <div
                v-if="idx < visibleSteps.length - 1"
                class="sett-step-line"
                :class="{ 'sett-step-line--done': isStepCompleted(step.number) }"
              ></div>
            </template>

            <!-- Rail footer: navigation + unlock -->
            <div class="sett-rail-foot">
              <LlSecondaryButton
                size="small"
                prepend-icon="mdi-arrow-left"
                class="flex-1"
                :disabled="activeStep <= 1 || settlementStore.isGenerated"
                @click="goToStep(prevStepBefore(activeStep))"
              >
                Back
              </LlSecondaryButton>
              <LlSecondaryButton
                v-if="settlementStore.isLocked && settlementStore.canReset"
                size="small"
                prepend-icon="mdi-lock-open-outline"
                class="flex-1"
                @click="showUnlockDialog = true"
              >
                Unlock to edit
              </LlSecondaryButton>
            </div>
          </div>

          <!-- Column 2: Step content panel (plain v-if; no Vuetify stepper window needed) -->
          <div class="sett-content-panel">
            <Transition name="sett-step-fade" mode="out-in">
            <div :key="activeStep" class="sett-step-transition-wrapper">
            <!-- Step content — wrapped to allow footer to sit outside and hide internal buttons -->
            <div class="sett-step-body">
            <!-- Step 1: Enter Settlement -->
            <template v-if="activeStep === 1">
              <!-- 7.1: No trust sub-account connected at matter level -->
              <v-card
                v-if="!isTrustConnected"
                flat
                class="border-0 !bg-[rgb(var(--v-theme-surface))]"
              >
                <v-card-title
                  class="bg-orange-lighten-4 text-orange-darken-3"
                >
                  <v-icon size="small" class="mr-1">mdi-alert</v-icon>
                  Step 1: Connect Trust Account
                </v-card-title>
                <v-card-text class="flex flex-col items-center py-10">
                  <div class="text-6xl mb-4">🏦</div>
                  <div
                    class="text-h6 font-weight-bold text-deep-orange-darken-1 mb-3"
                  >
                    No Trust Account Connected
                  </div>
                  <p
                    class="text-body-1 text-grey-darken-2 text-center mb-6"
                    style="max-width: 480px"
                  >
                    This matter's client does not have a trust sub-account
                    in QuickBooks. A trust account is required to record
                    settlement deposits and generate disbursements.
                  </p>
                  <LlSecondaryButton
                    color="primary"
                    data-cy="connect-trust-account"
                    @click="goToMatterTrust"
                  >
                    Connect Trust Account
                  </LlSecondaryButton>
                  <p
                    class="text-body-2 text-grey-darken-1 text-center mt-6"
                    style="max-width: 480px"
                  >
                    Once connected, you'll see the deposit selection table
                    and can begin the settlement.
                  </p>
                </v-card-text>
              </v-card>

              <!-- Normal deposit step -->
              <SettlementDepositStep
                v-else
                :deposits="settlementStore.deposits"
                :all-deposits-selected="settlementStore.allDepositsSelected"
                :recovery-amount="settlementStore.recoveryAmount"
                :fee-percentage="settingsForm.feePercentage ?? 0"
                :can-confirm="settlementStore.canConfirmStep1"
                :saving="settlementStore.saving"
                :validation-error="step1ValidationError"
                :is-client-level-billing="isClientLevelBilling"
                :client-accounting-enabled="isClientAccountingEnabled"
                :redesign-enabled="isSettlementRedesignEnabled"
                @toggle-all-deposits="settlementStore.toggleAllDeposits()"
                @toggle-deposit="settlementStore.toggleDeposit($event)"
                @open-trust-deposit="openTrustDeposit"
                @apply-date-range="onDepositDateRangeChange"
                @confirm-recovery="confirmRecovery"
              />
            </template>

            <!-- Step 2: Confirm Expenses -->
            <SettlementExpenseStep
              v-else-if="activeStep === 2"
              :expenses="settlementStore.expenses.items"
              :total-expenses="settlementStore.expenses.totalExpenses"
              :billed-expenses-total="settlementStore.expenses.billedExpensesTotal"
              :billed-expenses="settlementStore.expenses.billedItems"
              :expense-arrangement="settingsForm.expenseArrangement"
              :fee-method="settingsForm.feeMethod"
              :has-negative-fee-base="settlementStore.hasNegativeFeeBase"
              :saving="settlementStore.saving"
              :loading="settlementStore.expenses.loading"
              :company-expense-interest-rate="
                settlementStore.data?.companyExpenseInterestRate ?? null
              "
              :no-expense-interest="noExpenseInterest"
              :expense-from-date="settlementStore.expenses.expenseFromDate"
              :total-interest="settlementStore.totalInterest"
              :interest-confirmed="settlementStore.expenses.interestConfirmed"
              :confirmed-expense-from-date="
                settlementStore.expenses.confirmedExpenseFromDate
              "
              :confirmed-total-interest="
                settlementStore.expenses.confirmedTotalInterest
              "
              :expense-inline-enabled="isSettlementExpenseInlineEnabled"
              :redesign-enabled="isSettlementRedesignEnabled"
              :firm-name="meStore.company?.name || ''"
              :show-vendor="!!features.importcsv"
              :co-counsel-enabled="features.coCounsel"
              :co-counsel-expenses="settlementStore.coCounselExpenses"
              @confirm-interest="settlementStore.confirmInterest().catch(() => {})"
              @remove-interest="settlementStore.removeInterest().catch(() => {})"
              @confirm-expenses="confirmExpenses"
              @go-back="goToStep(1)"
              @open-add-expense="openAddExpense"
              @open-edit-expense="openEditExpense"
              @view-expenses="navigateToExpenses"
              @update:expense-from-date="onExpenseFromDateChange"
              @update:sort="onExpenseSort"
              @load-co-counsel-expenses="handleLoadCoCounselExpenses"
              @update:co-counsel-expense="handleUpdateCoCounselExpense"
            />

            <!-- Step 3: Record Attorney Fee -->
            <SettlementAttorneyFeeStep
              v-else-if="activeStep === 3"
              :redesign-enabled="isSettlementRedesignEnabled"
              :loading="settlementStore.attorneyFee.loading"
              :saving="settlementStore.saving"
              :recording="settlementStore.attorneyFee.recording"
              :calculated-fee="backendAttorneyFeeAmount"
              :existing-fee-amount="existingSettlementFeeAmount"
              :existing-fees="settlementStore.attorneyFee.existingFees"
              :fee-match-status="settlementStore.feeMatchStatus"
              :can-confirm="settlementStore.canConfirmStep3"
              :has-negative-fee-base="settlementStore.hasNegativeFeeBase"
              :fee-breakdown="feeBreakdown"
              :expense-label="expenseLabel"
              :expense-arrangement="settingsForm.expenseArrangement"
              :fee-percentage="settingsForm.feePercentage ?? 0"
              :round-to-nearest-dollar="settingsForm.roundToNearestDollar"
              :selected-charge-id="settlementStore.attorneyFee.selectedChargeId"
              :co-counsel-enabled="features.coCounsel"
              :fee-recipients="settlementStore.attorneyFee.feeRecipients"
              :is-recipients-confirmed="isRecipientsConfirmed"
              :is-recipients-locked="isRecipientsConfirmed"
              :no-matter-recipients-configured="settlementStore.attorneyFee.noMatterRecipientsConfigured"
              :firm-name="meStore.me?.company?.name ?? 'Your Firm'"
              @confirm-fee="confirmStep3"
              @go-back="goToStep(2)"
              @record-fee="handleRecordFee"
              @rounding-change="onRoundingChange"
              @manage-fixed-fees="navigateToFixedFees"
              @select-fee="handleSelectFee"
              @auto-confirm-fee="handleAutoConfirmFee"
              @load-fee-recipients="handleLoadFeeRecipients"
              @update:fee-recipients="handleUpdateFeeRecipients"
              @confirm-fee-recipients="handleConfirmFeeRecipients"
            />

            <!-- Step 4: Review Liens -->
            <template v-else-if="activeStep === 4">
              <SettlementLienStepFF
                :settlement-id="settlementStore.settlementId"
                :matter-id="matterId"
                :state="props.state"
                :redesign-enabled="isSettlementRedesignEnabled"
                @go-back="goToStep(3)"
                @confirmed="onLienStepConfirmed"
              />
            </template>

            <!-- Step 5: Map Payments -->
            <SettlementPaymentStep
              v-else-if="activeStep === 5"
              :domain="props.leanLawAuth?.domain || ''"
              :client-accounting-enabled="isClientAccountingEnabled"
              @go-back="goToStep(4)"
              @step-completed="handlePaymentStepCompleted"
            />

            <!-- Step 6: Generate Statement -->
            <template v-else-if="activeStep === 6">
              <!-- Per-matter narrative editor (part of the settlementV2 experience) -->
              <SettlementNarrativeSection
                v-if="showNarrativeEditor"
                :step-number="visibleStepCount"
                :settlement-id="settlementStore.settlementId"
                :is-locked="settlementStore.isLocked"
                :can-generate="settlementStore.canGenerateStatement"
                :saving="settlementStore.saving"
                @generate="handleGenerate"
                @go-back="goToStep(prevStepBefore(6))"
              />
              <!-- V2 host: settlementV2 post-settlement actions, OR the redesigned
                   disbursement table (settlementRedesign). -->
              <SettlementStatementStepV2
                v-else-if="isSettlementV2Enabled || isSettlementRedesignEnabled"
                :step-number="visibleStepCount"
                :show-post-settlement-actions="isSettlementV2Enabled"
                :use-disbursement-table="isSettlementRedesignEnabled"
                :statement-ready="settlementStore.isGenerated"
                :polling="settlementStore.statement.polling"
                :timed-out="settlementStore.statement.timedOut"
                :generating="settlementStore.statement.generating"
                :saving="settlementStore.saving"
                :download-url="settlementStore.statementDownloadUrl"
                :generated-date="settlementStore.statement.generatedAt"
                :can-generate="settlementStore.canGenerateStatement"
                :preview-url="settlementStore.previewDownloadUrl"
                :preview-generating="settlementStore.statement.previewGenerating"
                :preview-polling="settlementStore.statement.previewPolling"
                :preview-timed-out="settlementStore.statement.previewTimedOut"
                :can-reset="settlementStore.canReset"
                :reset-blocked-reason="settlementStore.resetBlockedReason"
                :invoice-finalized="settlementStore.invoiceFinalized"
                :can-finalize-invoice="settlementStore.canFinalizeInvoice"
                :can-undo-invoice="settlementStore.canUndoInvoice"
                :invoice-blocked-reason="settlementStore.invoiceBlockedReason"
                :trust-paid="settlementStore.trustPaid"
                :can-pay-from-trust="settlementStore.canPayFromTrust"
                :can-undo-trust-payment="settlementStore.canUndoTrustPayment"
                :trust-payment-blocked-reason="
                  settlementStore.trustPaymentBlockedReason
                "
                :undo-trust-payment-blocked-reason="
                  settlementStore.undoTrustPaymentBlockedReason
                "
                :qbo-pushed="settlementStore.qboPushed"
                :can-push-client-disbursement="
                  settlementStore.canPushClientDisbursement
                "
                :can-undo-client-disbursement="
                  settlementStore.canUndoClientDisbursement
                "
                :disbursement-blocked-reason="
                  settlementStore.disbursementBlockedReason
                "
                :undo-disbursement-blocked-reason="
                  settlementStore.undoDisbursementBlockedReason
                "
                :invoice-amount="settlementStore.invoiceBalance"
                :net-to-client="settlementStore.netToClient"
                :trust-balance="settlementStore.trustBalance"
                :trust-balance-loading="
                  settlementStore.postSettlement.trustBalanceLoading
                "
                :trust-balance-error="
                  settlementStore.postSettlement.trustBalanceError
                "
                :client-accounting-enabled="isClientAccountingEnabled"
                :trust-insufficient="settlementStore.trustInsufficient"
                :post-saving="settlementStore.postSettlement.saving"
                :post-error="settlementStore.postSettlement.error"
                :post-column-error="postSettlementColumnError"
                :saving-column="postSavingColumn"
                :trust-statement-loading="trustStatementDownloading"
                :stage-updated="stageUpdated"
                @generate="handleGenerate"
                @retry-polling="handleRetryPolling"
                @unlock-and-reset="showUnlockDialog = true"
                @download="handleStatementDownload"
                @download-trust-statement="handleTrustStatementDownload"
                @go-back="goToStep(prevStepBefore(6))"
                @retry-preview="handleRetryPreview"
                @finalize-invoice="handleFinalizeInvoice"
                @undo-invoice="showUndoDialog('undoInvoice')"
                @pay-trust="handlePayTrust"
                @undo-trust="showUndoDialog('undoTrust')"
                @push-qbo="handlePushQbo"
                @undo-qbo="showUndoDialog('undoQbo')"
                @view-invoice="handleViewInvoice"
              />
              <!-- V1: existing beta settlement step (unchanged) -->
              <SettlementStatementStep
                v-else
                :step-number="visibleStepCount"
                :statement-ready="settlementStore.isGenerated"
                :polling="settlementStore.statement.polling"
                :timed-out="settlementStore.statement.timedOut"
                :generating="settlementStore.statement.generating"
                :saving="settlementStore.saving"
                :download-url="settlementStore.statementDownloadUrl"
                :generated-date="settlementStore.statement.generatedAt"
                :can-generate="settlementStore.canGenerateStatement"
                :preview-url="settlementStore.previewDownloadUrl"
                :preview-generating="settlementStore.statement.previewGenerating"
                :preview-polling="settlementStore.statement.previewPolling"
                :preview-timed-out="settlementStore.statement.previewTimedOut"
                :can-reset="settlementStore.canReset"
                :reset-blocked-reason="settlementStore.resetBlockedReason"
                @generate="handleGenerate"
                @retry-polling="handleRetryPolling"
                @unlock-and-reset="showUnlockDialog = true"
                @reprint="handleReprint"
                @download="handleStatementDownload"
                @go-back="goToStep(prevStepBefore(6))"
                @retry-preview="handleRetryPreview"
              />
            </template>
            </div><!-- end sett-step-body -->
            </div><!-- end sett-step-transition-wrapper -->
            </Transition>
          </div>

          <!-- Column 3: Summary card -->
          <div>
            <SettlementSummaryCard v-bind="summaryCardProps" />

            <SettlementLivePreviewCard
              v-if="showNarrativeEditor && activeStep === 6"
              class="mt-4"
            />
          </div>
        </div>
        <v-stepper
          v-else
          v-model="activeStep"
          alt-labels
          non-linear
          flat
          hide-actions
          bg-color="transparent"
          class="settlement-stepper"
        >
          <v-stepper-header>
            <template v-for="(step, idx) in visibleSteps" :key="step.number">
              <v-stepper-item
                :title="step.label"
                :value="step.number"
                :complete="isStepCompleted(step.number)"
                :color="activeStep === step.number || isStepCompleted(step.number) ? 'primary' : undefined"
                :class="{ 'cursor-pointer': canNavigateToStep(step.number) }"
                @click="handleRailStepClick(step.number)"
              />
              <v-divider
                v-if="idx < visibleSteps.length - 1"
                :class="isStepCompleted(step.number) ? 'step-line-green' : 'step-line-default'"
              />
            </template>
          </v-stepper-header>

          <div class="grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-6 mt-2">
            <v-card variant="outlined" elevation="1" class="bg-gray-50 border-gray-300">
              <v-stepper-window>
                <v-stepper-window-item :value="1">
                  <v-card v-if="!isTrustConnected" flat class="border-0 !bg-[rgb(var(--v-theme-surface))]">
                    <v-card-title class="bg-orange-lighten-4 text-orange-darken-3">
                      <v-icon size="small" class="mr-1">mdi-alert</v-icon>
                      Step 1: Connect Trust Account
                    </v-card-title>
                    <v-card-text class="flex flex-col items-center py-10">
                      <div class="text-6xl mb-4">🏦</div>
                      <div class="text-h6 font-weight-bold text-deep-orange-darken-1 mb-3">No Trust Account Connected</div>
                      <p class="text-body-1 text-grey-darken-2 text-center mb-6" style="max-width: 480px">
                        This matter's client does not have a trust sub-account in QuickBooks. A trust account is required to record settlement deposits and generate disbursements.
                      </p>
                      <LlSecondaryButton color="primary" data-cy="connect-trust-account" @click="goToMatterTrust">Connect Trust Account</LlSecondaryButton>
                      <p class="text-body-2 text-grey-darken-1 text-center mt-6" style="max-width: 480px">Once connected, you'll see the deposit selection table and can begin the settlement.</p>
                    </v-card-text>
                  </v-card>
                  <SettlementDepositStep
                    v-else
                    :deposits="settlementStore.deposits"
                    :all-deposits-selected="settlementStore.allDepositsSelected"
                    :recovery-amount="settlementStore.recoveryAmount"
                    :fee-percentage="settingsForm.feePercentage ?? 0"
                    :can-confirm="settlementStore.canConfirmStep1"
                    :saving="settlementStore.saving"
                    :validation-error="step1ValidationError"
                    :is-client-level-billing="isClientLevelBilling"
                    :client-accounting-enabled="isClientAccountingEnabled"
                    :redesign-enabled="isSettlementRedesignEnabled"
                    @toggle-all-deposits="settlementStore.toggleAllDeposits()"
                    @toggle-deposit="settlementStore.toggleDeposit($event)"
                    @open-trust-deposit="openTrustDeposit"
                    @apply-date-range="onDepositDateRangeChange"
                    @confirm-recovery="confirmRecovery"
                  />
                </v-stepper-window-item>

                <v-stepper-window-item :value="2">
                  <SettlementExpenseStep
                    :expenses="settlementStore.expenses.items"
                    :total-expenses="settlementStore.expenses.totalExpenses"
                    :billed-expenses-total="settlementStore.expenses.billedExpensesTotal"
                    :billed-expenses="settlementStore.expenses.billedItems"
                    :expense-arrangement="settingsForm.expenseArrangement"
                    :fee-method="settingsForm.feeMethod"
                    :has-negative-fee-base="settlementStore.hasNegativeFeeBase"
                    :saving="settlementStore.saving"
                    :loading="settlementStore.expenses.loading"
                    :company-expense-interest-rate="settlementStore.data?.companyExpenseInterestRate ?? null"
                    :no-expense-interest="noExpenseInterest"
                    :expense-from-date="settlementStore.expenses.expenseFromDate"
                    :total-interest="settlementStore.totalInterest"
                    :interest-confirmed="settlementStore.expenses.interestConfirmed"
                    :confirmed-expense-from-date="settlementStore.expenses.confirmedExpenseFromDate"
                    :confirmed-total-interest="settlementStore.expenses.confirmedTotalInterest"
                    :expense-inline-enabled="isSettlementExpenseInlineEnabled"
                    :redesign-enabled="isSettlementRedesignEnabled"
                    :firm-name="meStore.company?.name || ''"
                    :show-vendor="!!features.importcsv"
                    :co-counsel-enabled="features.coCounsel"
                    :co-counsel-expenses="settlementStore.coCounselExpenses"
                    @confirm-interest="settlementStore.confirmInterest().catch(() => {})"
                    @remove-interest="settlementStore.removeInterest().catch(() => {})"
                    @confirm-expenses="confirmExpenses"
                    @go-back="goToStep(1)"
                    @open-add-expense="openAddExpense"
                    @open-edit-expense="openEditExpense"
                    @view-expenses="navigateToExpenses"
                    @update:expense-from-date="onExpenseFromDateChange"
                    @update:sort="onExpenseSort"
                    @load-co-counsel-expenses="handleLoadCoCounselExpenses"
                    @update:co-counsel-expense="handleUpdateCoCounselExpense"
                  />
                </v-stepper-window-item>

                <v-stepper-window-item :value="3">
                  <SettlementAttorneyFeeStep
                    :redesign-enabled="isSettlementRedesignEnabled"
                    :loading="settlementStore.attorneyFee.loading"
                    :saving="settlementStore.saving"
                    :recording="settlementStore.attorneyFee.recording"
                    :calculated-fee="backendAttorneyFeeAmount"
                    :existing-fee-amount="existingSettlementFeeAmount"
                    :existing-fees="settlementStore.attorneyFee.existingFees"
                    :fee-match-status="settlementStore.feeMatchStatus"
                    :can-confirm="settlementStore.canConfirmStep3"
                    :has-negative-fee-base="settlementStore.hasNegativeFeeBase"
                    :fee-breakdown="feeBreakdown"
                    :expense-label="expenseLabel"
                    :expense-arrangement="settingsForm.expenseArrangement"
                    :fee-percentage="settingsForm.feePercentage ?? 0"
                    :round-to-nearest-dollar="settingsForm.roundToNearestDollar"
                    :selected-charge-id="settlementStore.attorneyFee.selectedChargeId"
                    :co-counsel-enabled="features.coCounsel"
                    :fee-recipients="settlementStore.attorneyFee.feeRecipients"
                    :is-recipients-confirmed="isRecipientsConfirmed"
                    :is-recipients-locked="isRecipientsConfirmed"
                    :no-matter-recipients-configured="settlementStore.attorneyFee.noMatterRecipientsConfigured"
                    :firm-name="meStore.me?.company?.name ?? 'Your Firm'"
                    @confirm-fee="confirmStep3"
                    @go-back="goToStep(2)"
                    @record-fee="handleRecordFee"
                    @rounding-change="onRoundingChange"
                    @manage-fixed-fees="navigateToFixedFees"
                    @select-fee="handleSelectFee"
                    @auto-confirm-fee="handleAutoConfirmFee"
                    @load-fee-recipients="handleLoadFeeRecipients"
                    @update:fee-recipients="handleUpdateFeeRecipients"
                    @confirm-fee-recipients="handleConfirmFeeRecipients"
                  />
                </v-stepper-window-item>

                <v-stepper-window-item :value="4">
                  <SettlementLienStepFF
                    :settlement-id="settlementStore.settlementId"
                    :matter-id="matterId"
                    :state="props.state"
                    :redesign-enabled="isSettlementRedesignEnabled"
                    @go-back="goToStep(3)"
                    @confirmed="onLienStepConfirmed"
                  />
                </v-stepper-window-item>

                <v-stepper-window-item :value="5">
                  <SettlementPaymentStep
                    v-if="activeStep === 5"
                    :domain="props.leanLawAuth?.domain || ''"
                    :client-accounting-enabled="isClientAccountingEnabled"
                    @go-back="goToStep(4)"
                    @step-completed="handlePaymentStepCompleted"
                  />
                </v-stepper-window-item>

                <v-stepper-window-item :value="6">
                  <SettlementNarrativeSection
                    v-if="showNarrativeEditor && activeStep === 6"
                    :step-number="visibleStepCount"
                    :settlement-id="settlementStore.settlementId"
                    :is-locked="settlementStore.isLocked"
                    :can-generate="settlementStore.canGenerateStatement"
                    :saving="settlementStore.saving"
                    @generate="handleGenerate"
                    @go-back="goToStep(prevStepBefore(6))"
                  />
                  <SettlementStatementStepV2
                    v-else-if="isSettlementV2Enabled"
                    :step-number="visibleStepCount"
                    :show-post-settlement-actions="isSettlementV2Enabled"
                    :statement-ready="settlementStore.isGenerated"
                    :polling="settlementStore.statement.polling"
                    :timed-out="settlementStore.statement.timedOut"
                    :generating="settlementStore.statement.generating"
                    :saving="settlementStore.saving"
                    :download-url="settlementStore.statementDownloadUrl"
                    :generated-date="settlementStore.statement.generatedAt"
                    :can-generate="settlementStore.canGenerateStatement"
                    :preview-url="settlementStore.previewDownloadUrl"
                    :preview-generating="settlementStore.statement.previewGenerating"
                    :preview-polling="settlementStore.statement.previewPolling"
                    :preview-timed-out="settlementStore.statement.previewTimedOut"
                    :can-reset="settlementStore.canReset"
                    :reset-blocked-reason="settlementStore.resetBlockedReason"
                    :invoice-finalized="settlementStore.invoiceFinalized"
                    :can-finalize-invoice="settlementStore.canFinalizeInvoice"
                    :can-undo-invoice="settlementStore.canUndoInvoice"
                    :invoice-blocked-reason="settlementStore.invoiceBlockedReason"
                    :trust-paid="settlementStore.trustPaid"
                    :can-pay-from-trust="settlementStore.canPayFromTrust"
                    :can-undo-trust-payment="settlementStore.canUndoTrustPayment"
                    :trust-payment-blocked-reason="settlementStore.trustPaymentBlockedReason"
                    :undo-trust-payment-blocked-reason="settlementStore.undoTrustPaymentBlockedReason"
                    :qbo-pushed="settlementStore.qboPushed"
                    :can-push-client-disbursement="settlementStore.canPushClientDisbursement"
                    :can-undo-client-disbursement="settlementStore.canUndoClientDisbursement"
                    :disbursement-blocked-reason="settlementStore.disbursementBlockedReason"
                    :undo-disbursement-blocked-reason="settlementStore.undoDisbursementBlockedReason"
                    :invoice-amount="settlementStore.invoiceBalance"
                    :net-to-client="settlementStore.netToClient"
                    :trust-balance="settlementStore.trustBalance"
                    :trust-balance-loading="settlementStore.postSettlement.trustBalanceLoading"
                    :trust-balance-error="settlementStore.postSettlement.trustBalanceError"
                    :client-accounting-enabled="isClientAccountingEnabled"
                    :trust-insufficient="settlementStore.trustInsufficient"
                    :post-saving="settlementStore.postSettlement.saving"
                    :post-error="settlementStore.postSettlement.error"
                    :post-column-error="postSettlementColumnError"
                    :saving-column="postSavingColumn"
                    :trust-statement-loading="trustStatementDownloading"
                    :stage-updated="stageUpdated"
                    @generate="handleGenerate"
                    @retry-polling="handleRetryPolling"
                    @unlock-and-reset="showUnlockDialog = true"
                    @download="handleStatementDownload"
                    @download-trust-statement="handleTrustStatementDownload"
                    @go-back="goToStep(prevStepBefore(6))"
                    @retry-preview="handleRetryPreview"
                    @finalize-invoice="handleFinalizeInvoice"
                    @undo-invoice="showUndoDialog('undoInvoice')"
                    @pay-trust="handlePayTrust"
                    @undo-trust="showUndoDialog('undoTrust')"
                    @push-qbo="handlePushQbo"
                    @undo-qbo="showUndoDialog('undoQbo')"
                    @view-invoice="handleViewInvoice"
                  />
                  <SettlementStatementStep
                    v-else
                    :step-number="visibleStepCount"
                    :statement-ready="settlementStore.isGenerated"
                    :polling="settlementStore.statement.polling"
                    :timed-out="settlementStore.statement.timedOut"
                    :generating="settlementStore.statement.generating"
                    :saving="settlementStore.saving"
                    :download-url="settlementStore.statementDownloadUrl"
                    :generated-date="settlementStore.statement.generatedAt"
                    :can-generate="settlementStore.canGenerateStatement"
                    :preview-url="settlementStore.previewDownloadUrl"
                    :preview-generating="settlementStore.statement.previewGenerating"
                    :preview-polling="settlementStore.statement.previewPolling"
                    :preview-timed-out="settlementStore.statement.previewTimedOut"
                    :can-reset="settlementStore.canReset"
                    :reset-blocked-reason="settlementStore.resetBlockedReason"
                    @generate="handleGenerate"
                    @retry-polling="handleRetryPolling"
                    @unlock-and-reset="showUnlockDialog = true"
                    @reprint="handleReprint"
                    @download="handleStatementDownload"
                    @go-back="goToStep(prevStepBefore(6))"
                    @retry-preview="handleRetryPreview"
                  />
                </v-stepper-window-item>
              </v-stepper-window>
            </v-card>

            <div class="border border-gray-200 rounded overflow-hidden self-start">
              <SettlementSummaryCard
                v-bind="summaryCardProps"
              />
              <SettlementLivePreviewCard
                v-if="showNarrativeEditor && activeStep === 6"
                class="mt-4"
              />
            </div>
          </div>
        </v-stepper>
        <!-- ─── End legacy stepper ─────────────────────────────────────── -->

        <!-- ─── Settings Change Confirmation Dialog ───────── -->
        <LlConfirmationDialog
          v-model="showSettingsResetDialog"
          title="Reset Completed Steps?"
          message="Changing settlement settings will reset all completed steps. You will need to re-confirm each step with the new settings."
          confirm-text="Reset & Change Settings"
          toggle-text="Cancel"
          @confirm="confirmSettingsChange"
          @cancel="revertSettingsChange"
        />

        <!-- ─── Unlock Confirmation Dialog ────────────────── -->
        <LlConfirmationDialog
          v-model="showUnlockDialog"
          :title="unlockDialogTitle"
          :message="unlockDialogMessage"
          confirm-text="Unlock & Reset"
          toggle-text="Cancel"
          @confirm="confirmUnlock"
          @cancel="showUnlockDialog = false"
        />

        <!-- ─── Post-Settlement Action Dialogs ───────────── -->
        <LlConfirmationDialog
          v-model="postSettlementDialog.show"
          :title="postSettlementDialog.title"
          :message="postSettlementDialog.message"
          :confirm-text="postSettlementDialog.confirmText"
          toggle-text="Cancel"
          @confirm="confirmPostSettlementAction"
          @cancel="postSettlementDialog.show = false"
        />

        <!-- ─── Record New Deposit (TrustDeposit modal) ──────────── -->
        <!-- SC-45762: settlement-context hides client/matter picker and pre-fills description -->
        <TrustDeposit
          v-if="showDepositDialog"
          :state="props.state"
          :state-params="props.stateParams"
          :user-service="props.userService"
          :lean-law-api="props.leanLawApi"
          :alerts="props.alerts"
          :on-saved="onDepositSaved"
          :on-cancel="closeDepositDialog"
          :settlement-context="true"
        />

        <!-- ─── Add / Edit Expense Dialog (SC-45761) ────────── -->
        <v-dialog v-if="isSettlementExpenseInlineEnabled" v-model="showExpenseDialog" max-width="640" persistent>
          <ExpenseEdit
            v-if="showExpenseDialog"
            :state-params="{}"
            :selected-expense="editingExpenseId ? { id: editingExpenseId } : {}"
            :selected-matter="editingExpenseId ? undefined : (matterId || undefined)"
            :lean-law-api="props.leanLawApi"
            :state="props.state || {}"
            :tracking="props.tracking || {}"
            :settlement-context="true"
            @filter="onExpenseSaved"
            @navigate-to-expenses="navigateToExpenses"
          />
        </v-dialog>

        <!-- ─── Update Matter Stage Dialog ────────────────── -->
        <UpdateMatterStageDialog
          v-if="isSettlementV2Enabled"
          v-model="showUpdateStageDialog"
          :current-stage="(matterData as any)?.stage ?? 'Active'"
          @not-now="handleStageUpdateNotNow"
          @update-stage="handleStageUpdate"
        /> </template
      ><!-- end v-else wizard gate (isTrustEnabled) -->
    </v-container>
  </div>
</template>

<script setup lang="ts">
import {
  reactive,
  ref,
  computed,
  nextTick,
  watch,
  onMounted,
  onActivated,
  onBeforeUnmount,
} from 'vue';
import { useCompanyStore } from '@/services/UserService/useCompanyStore';
import { useSettlementStore } from '@/modules/matters/settlement/stores/useSettlementStore';
import useClientMatterStore from '@/services/useClientMatterStore';
import { useMeStore } from '@/services/UserService/useMeStore';
import { useSnackbarHandler } from '@/composables/useSnackbarHandler';
import LlSecondaryButton from '@/components/LlSecondaryButton.vue';
import LlConfirmationDialog from '@/components/LlConfirmationDialog.vue';
import LlIcon from '@/components/LlIcon.vue';
import TrustDeposit from '@/modules/accounting/TrustDeposit.vue';
import ExpenseEdit from '@/modules/expenses/ExpenseEdit.vue';
import SkeletonRenderer from '@/components/SkeletonRenderer.vue';
import SettlementSettingsModal from './components/SettlementSettingsModal.vue';
import SettlementDepositStep from './components/SettlementDepositStep.vue';
import SettlementExpenseStep from './components/SettlementExpenseStep.vue';
import SettlementLienStepFF from './components/SettlementLienStepFF.vue';
import SettlementAttorneyFeeStep from './components/SettlementAttorneyFeeStep.vue';
import SettlementPaymentStep from './components/SettlementPaymentStep.vue';
import SettlementSummaryCard from './components/SettlementSummaryCard.vue';
import { useSettlementSummaryCardProps } from './composables/useSettlementSummaryCardProps';
import SettlementStatementStep from './components/SettlementStatementStep.vue';
import SettlementStatementStepV2 from './components/SettlementStatementStepV2.vue';
import SettlementNarrativeSection from './components/SettlementNarrativeSection.vue';
import SettlementLivePreviewCard from './components/SettlementLivePreviewCard.vue';
import SettlementGate from './components/SettlementGate.vue';
import UpdateMatterStageDialog from './components/UpdateMatterStageDialog.vue';
import LlTooltip from '@/components/LlTooltip.vue';
import { fetchContingencyOptions, getCaseStatusLabel } from '@/services/constants/contingencyOptions';
import { getClientMatterCount } from '@/services/settlement/SettlementService';
import { formatCurrency } from '@/services/utils';
import { format, isValid, parseISO, startOfYear } from 'date-fns';
import EventTracker from '@/services/eventTracker';
import {
  TrackingCategory,
  TrackingAction,
} from '@/constants/trackingConstants';
import { FEE_METHOD, EXPENSE_ARRANGEMENT } from '@/constants/settlementConstants';
import type {
  ComputedBreakdown,
  FeeMethod,
  ExpenseArrangement,
  UpdateSettlementRequest,
  FeeRecipient,
} from '@/models/settlementTypes';

interface PendingSettings {
  feeMethod: FeeMethod | null;
  expenseArrangement: ExpenseArrangement | null;
  feePercentage?: number | null;
  settlementDate?: string | null;
}

// ── Props (injected by vuetify() helper) ─────────────────
const props = defineProps({
  stateParams: { type: Object, required: false },
  state: { type: Object, required: false },
  clientMatterManager: { type: Object, required: false },
  userService: { type: Object, required: false },
  viewInvoiceService: { type: Object, required: false },
  leanLawApi: { type: Object, required: false },
  leanLawAuth: { type: Object, required: false },
  alerts: { type: Object, required: false },
  tracking: { type: Object, required: false },
});

// ── Stores & Composables ─────────────────────────────────
const settlementStore = useSettlementStore();
const meStore = useMeStore();
const companyStore = useCompanyStore();
// Always fetch latest user/company data when entering the tab
async function refreshUserAndCompany() {
  if (!matterId.value) return;
  // Always force-refresh user, company, and matter data
  await Promise.all([meStore.getMe(true), companyStore.loadMyCompany(true)]);
}
const clientMatterStore = useClientMatterStore();
const { handleError, handleSuccess } = useSnackbarHandler();

// ── Local UI State ───────────────────────────────────────
const uiState = reactive({
  loading: true,
  // Whether the last loadSettlementData() ran to completion. loading alone is not
  // enough: it is cleared on the bail-out and error paths too.
  loadSucceeded: false,
});

const activeStep = ref(1);
const settingsModalRef = ref<{ open(focusField?: string): void; close(): void } | null>(null);
const caseStatusOptions = ref<Array<{ value: string; label: string }>>([]);
const savingSettings = ref(false);

/** Case Status lives on the Matter, not the Settlement record — same options service
 *  MatterInfo.vue uses, so the cache is shared/warm across both tabs. */
function loadCaseStatusOptions() {
  if (!isContingencyMatter.value) return;
  fetchContingencyOptions()
    .then(({ caseStatuses }) => {
      caseStatusOptions.value = caseStatuses;
    })
    .catch(() => {
      handleError('Failed to load case status options. Please refresh the page.');
    });
}

// The panel now owns its own draft/open state (SettlementSettingsModal.vue) — these are
// thin wrappers so the "Settings" button and stat chips don't need to know that.
function openSettingsPanel() {
  loadCaseStatusOptions();
  settingsModalRef.value?.open();
}

function openSettingsAndFocus(fieldKey: string) {
  loadCaseStatusOptions();
  settingsModalRef.value?.open(fieldKey);
}
const showSettingsResetDialog = ref(false);
const showUnlockDialog = ref(false);
const step1ValidationError = ref('');

const unlockDialogTitle = computed(() =>
  settlementStore.isLocked ? 'Unlock & Reset Settlement?' : 'Reset Settlement Steps?',
);
const unlockDialogMessage = computed(() =>
  settlementStore.isLocked
    ? 'This will reset all steps so you can make changes and generate a new statement. The existing settlement statement will be removed.'
    : 'This will reset all completed steps so you can make changes.',
);

// Matter data (loaded via clientMatterManager to check trust connection)
const matterData = ref<any>(null);
const clientMatterCount = ref<number | null>(null);

// ── Deposit Dialog State ─────────────────────────────────
const showDepositDialog = ref(false);

// ── Expense Dialog State (SC-45761) ──────────────────────
const showExpenseDialog = ref(false);
/** null = create new; string = edit existing expense by id */
const editingExpenseId = ref<string | null>(null);

// Locally‑completed steps (for placeholder steps 2–5 that don't yet persist to the store)
const localCompletedSteps = ref<number[]>([]);

// Pending settings change (held until confirmation)
const pendingSettings = ref<PendingSettings | null>(null);
// Held alongside pendingSettings when the reset-confirmation dialog is showing —
// applied together with pendingSettings on confirm, discarded on revert.
const pendingNoExpenseInterest = ref<boolean | null>(null);
// undefined = as-of date unchanged; null = explicitly cleared; string = a chosen date.
// Using undefined (not null) as the "unchanged" sentinel lets a cleared date (null) still
// be applied through the reset path instead of being skipped.
const pendingExpenseFromDate = ref<string | null | undefined>(undefined);
// Same undefined/null/value convention as pendingExpenseFromDate. Case Status normally
// saves immediately and independently of the reset-confirmation flow (it never triggers
// that dialog on its own) — but if the dialog is ALREADY showing because some other field
// needs it, a case-status edit made in the same Save must wait for that same decision
// instead of committing to the backend before the user has confirmed anything.
const pendingCaseStatus = ref<string | null | undefined>(undefined);

// Post-settlement action dialog state
type PostSettlementActionKey = 'undoInvoice' | 'undoTrust' | 'undoQbo';

const postSettlementDialog = reactive({
  show: false,
  title: '',
  message: '',
  confirmText: '',
  action: null as PostSettlementActionKey | null,
});

const postSettlementColumnError = ref<string | null>(null);

// Update Matter Stage dialog state
const showUpdateStageDialog = ref(false);
const stageUpdated = ref(false);
const trustStatementDownloading = ref(false);

const postSavingColumn = ref<string | null>(null);

// Settings form (local model synced to store data — defaults overwritten by syncSettingsFromStore)
const settingsForm = reactive({
  feePercentage: null as number | null,
  feeMethod: null as FeeMethod | null,
  expenseArrangement: null as ExpenseArrangement | null,
  roundToNearestDollar: false,
  settlementDate: null as string | null,
});

// ── Constants ────────────────────────────────────────────
/** Step number reserved for Map Payments — skipped when payments mapping is not applicable. */
const MAP_PAYMENTS_STEP = 5;

const stepDefinitions = [
  { number: 1, label: 'Enter Settlement' },
  { number: 2, label: 'Confirm Expenses' },
  { number: 3, label: 'Record Attorney Fee' },
  { number: 4, label: 'Review Liens' },
  { number: 5, label: 'Map Payments' },
  { number: 6, label: 'Generate Statement' },
];

// ── Analytics Tracking ───────────────────────────────────
/** Timestamp (epoch ms) when wizard was started (Step 1 confirmed). */
let wizardStartedAt: number | null = null;

/** Timestamp (epoch ms) when the current wizard step was viewed (for per-step durationSeconds). */
// Keyed by step number, not a single value: confirming a step advances activeStep,
// which fires the next step's Step Viewed in the same tick. A shared timestamp meant a
// step's completion read whichever step was viewed last.
const stepViewedAt = new Map<number, number>();

function stepDurationSeconds(stepNumber: number): number | null {
  const viewedAt = stepViewedAt.get(stepNumber);
  return viewedAt ? Math.round((Date.now() - viewedAt) / 1000) : null;
}

/** Guard-wrapped tracker. All events include matterId. */
function trackSettlement(
  category: string,
  action: string,
  extra: Record<string, unknown> = {},
) {
  const payload = { matterId: matterId.value, ...extra };
  if (!props.tracking) return;
  EventTracker.trackEvent(props.tracking, category, action, payload);
}

// ── Computed ─────────────────────────────────────────────
const matterId = computed(
  () => props.stateParams?.matterId ?? props.stateParams?.id ?? null,
);

/** Feature flags from the /me endpoint. */
const features = computed(
  () => meStore.me?.features || ({} as Record<string, boolean>),
);

/** Parent flipper for the settlement V2 experience. */
const isSettlementV2Enabled = computed(() => features.value.settlementV2);


const isSettlementRedesignEnabled = computed(() => features.value.settlementRedesign);

/** Inline add/edit expenses from within the settlement wizard (SC-45761).
 *  Only active when the settlementRedesign flag is also on — the two features
 *  ship together and the inline UX makes no sense without the redesign. */
const isSettlementExpenseInlineEnabled = computed(
  () => isSettlementRedesignEnabled.value,
);

/** Flipper for the per-matter Step 6 narrative editor.
 *  Folded into the settlementV2 experience rather than the shared
 *  firm-level settlement-template flag. */
const isSettlementNarrativeEnabled = computed(
  () => isSettlementV2Enabled.value,
);

/** Show the narrative editor only in the pre-generation idle state of Step 6. */
const showNarrativeEditor = computed(
  () =>
    isSettlementNarrativeEnabled.value &&
    !settlementStore.isGenerated &&
    !settlementStore.statement.generating &&
    !settlementStore.statement.polling &&
    !settlementStore.statement.timedOut,
);


/** Firm-level trust accounting check — same flag used across all Angular controllers.
 *  CompanyDetail class doesn't type `.settings` — the raw API response includes it. */
const isTrustEnabled = computed(
  () => !!(meStore.company as any)?.settings?.trustEnabled,
);

/** Matter-level trust sub-account check (Story 7.1).
 *  True when this matter (or its client) has a trust sub-account in QBO. */
const isTrustConnected = computed(
  () =>
    !!(
      matterData.value?.billing?.trust ||
      matterData.value?.client?.billing?.trust
    ),
);

/** True when the client uses client-level billing — pre-V4 copy paths only (SC-46858). */
const isClientLevelBilling = computed(
  () => !!matterData.value?.client?.billing?.billClient,
);

/** V4 flipper: on, trust copy drops its matter-only wording (SC-46858). Delete with it. */
const isClientAccountingEnabled = computed(
  () => !!features.value.settlementClientAccounting,
);

/** Step 1 withholds the deposit list for a client-level client until the flipper is on.
 *  Deposits are not fetched at all in that state: loadDeposits auto-selects everything it
 *  returns, which would drive a recovery amount — and the fee and client net derived from
 *  it — into the summary card, defeating the point of withholding the list. */
const isClientAccountingBlocked = computed(
  () => !isClientAccountingEnabled.value && isClientLevelBilling.value,
);

/** True when the matter has opted out of expense interest — sourced from settlement API response. */
const noExpenseInterest = computed(
  () => settlementStore.data?.noExpenseInterest ?? false,
);

/** Permission check for settlement tab (matches old Angular tab logic) */
const hasSettlementPermission = computed(() => {
  // Features and permissions are on meStore.me
  const features = meStore.me?.features || {};
  // Most LeanLaw permissions are flat, e.g. billingCreate
  const billingCreate = (meStore.me?.permissions as any)?.billing?.create;
  // Must have the feature flag and billing.create permission`
  return !!(features.settlementTab && billingCreate);
});

const isMapPaymentsDisabled = computed(
  () => settingsForm.expenseArrangement !== EXPENSE_ARRANGEMENT.CLIENT,
);

/** Company-level expense interest rate (%) from firm settings. */
const companyExpenseInterestRate = computed<number | null>(
  () => (meStore.company as any)?.settings?.fees?.expenseInterest ?? null,
);

/** True when expense interest is explicitly disabled on this matter. */
const matterNoExpenseInterest = computed<boolean>(
  () => !!matterData.value?.billing?.contingency?.noExpenseInterest,
);

/** The Settlement tab has no route-level guard restricting it to contingency
 *  matters (only a nav-visibility check), so this is a real guard, not redundant. */
const isContingencyMatter = computed<boolean>(
  () => matterData.value?.billing?.type === 'contingency',
);

// ── Stats bar display values ─────────────────────────────
const feePercentageDisplay = computed(() =>
  settingsForm.feePercentage != null ? `${settingsForm.feePercentage}%` : '—',
);

const feeMethodDisplay = computed(() => {
  if (settingsForm.feeMethod === FEE_METHOD.GROSS) return 'Gross';
  if (settingsForm.feeMethod === FEE_METHOD.NET) return 'Net of Expenses';
  return '—';
});

const expenseArrangementDisplay = computed(() => {
  if (settingsForm.expenseArrangement === EXPENSE_ARRANGEMENT.FIRM) return 'Firm advances';
  if (settingsForm.expenseArrangement === EXPENSE_ARRANGEMENT.CLIENT) return 'Client pays';
  if (settingsForm.expenseArrangement === EXPENSE_ARRANGEMENT.ABSORBED) return 'Absorbed';
  return '—';
});

const settlementDateDisplay = computed(() => {
  const dateStr = (settlementStore.data as any)?.settlementDate;
  if (!dateStr) return '—';
  const parsed = parseISO(dateStr);
  return isValid(parsed) ? format(parsed, 'MM/dd/yyyy') : '—';
});

const expenseInterestRateDisplay = computed(() => {
  if (matterNoExpenseInterest.value) return 'Off';
  const rate = companyExpenseInterestRate.value;
  return rate ? `${rate}%` : '—';
});

const caseStatusDisplay = computed(
  () => getCaseStatusLabel(matterData.value?.caseStatus) || '—',
);

const feeMethodFullLabel = computed(() => {
  if (settingsForm.feeMethod === FEE_METHOD.GROSS)
    return 'Gross — fee calculated on full settlement amount';
  if (settingsForm.feeMethod === FEE_METHOD.NET)
    return 'Net of Expenses — fee calculated after deducting costs';
  return null;
});

const expenseArrangementFullLabel = computed(() => {
  if (settingsForm.expenseArrangement === EXPENSE_ARRANGEMENT.FIRM)
    return 'Firm advances all expenses';
  if (settingsForm.expenseArrangement === EXPENSE_ARRANGEMENT.CLIENT)
    return 'Client pays expenses as they go';
  if (settingsForm.expenseArrangement === EXPENSE_ARRANGEMENT.ABSORBED)
    return 'Firm absorbs expenses (from fee)';
  return null;
});

interface StatChip {
  icon: string;
  display: string;
  label: string;
  tooltipValue?: string;
  description: string;
  tooltipMaxWidth: number;
  focusField: string;
}

const settlementStats = computed<StatChip[]>(() => {
  const stats: StatChip[] = [
    {
      icon: 'mdi-percent',
      display: feePercentageDisplay.value,
      label: 'Contingency Fee',
      tooltipValue: feePercentageDisplay.value,
      description: 'Percentage applied to the recovery amount.',
      tooltipMaxWidth: 220,
      focusField: 'feePercentage',
    },
    {
      icon: 'mdi-file-document-outline',
      display: feeMethodDisplay.value,
      label: 'Fee Calculation Method',
      tooltipValue: feeMethodFullLabel.value ?? undefined,
      description: 'How the attorney fee is calculated from the settlement.',
      tooltipMaxWidth: 240,
      focusField: 'feeMethod',
    },
    {
      icon: 'mdi-briefcase-outline',
      display: expenseArrangementDisplay.value,
      label: 'Expense Arrangement',
      tooltipValue: expenseArrangementFullLabel.value ?? undefined,
      description: 'How case expenses are handled in the settlement.',
      tooltipMaxWidth: 240,
      focusField: 'expenseArrangement',
    },
    {
      icon: 'mdi-calendar',
      display: settlementDateDisplay.value,
      label: 'Settlement Date',
      tooltipValue: settlementDateDisplay.value,
      description:
        'The date the settlement was reached. Shown on the settlement statement.',
      tooltipMaxWidth: 220,
      focusField: 'settlementDate',
    },
  ];

  if (companyExpenseInterestRate.value) {
    stats.push({
      icon: 'mdi-cash-clock',
      display: expenseInterestRateDisplay.value,
      label: 'Expense Interest Rate',
      tooltipValue: expenseInterestRateDisplay.value,
      description: matterNoExpenseInterest.value
        ? 'Interest is disabled for this matter.'
        : 'Rate applied to firm-advanced expenses. Set in Firm Setup › Billing.',
      tooltipMaxWidth: 240,
      focusField: 'expenseInterest',
    });
  }

  if (isContingencyMatter.value) {
    stats.unshift({
      icon: 'mdi-progress-check',
      display: caseStatusDisplay.value,
      label: 'Case Status',
      tooltipValue: caseStatusDisplay.value,
      description: 'Where this matter stands in the litigation lifecycle.',
      tooltipMaxWidth: 220,
      focusField: 'caseStatus',
    });
  }

  return stats;
});

/** Merged completed steps from store (API-persisted) + local placeholders, deduplicated. */
const allCompletedSteps = computed((): number[] => {
  const seen = new Set<number>(settlementStore.completedSteps);
  localCompletedSteps.value.forEach((n) => seen.add(n));
  return Array.from(seen);
});

// ── SC-45776: Step rail computed ─────────────────────────
/** Steps the user can see — Map Payments is dropped (and the rest renumbered) when not applicable. */
const visibleSteps = computed(() =>
  stepDefinitions
    .filter((s) => !(s.number === MAP_PAYMENTS_STEP && isMapPaymentsDisabled.value))
    .map((s, i) => ({ ...s, displayNumber: i + 1 })),
);

const visibleStepCount = computed(() => visibleSteps.value.length);

/** Progress bar fill percentage for the step rail header. */
const railProgressPercent = computed(() => {
  const completedVisible = allCompletedSteps.value.filter(
    (s) => !(s === MAP_PAYMENTS_STEP && isMapPaymentsDisabled.value),
  ).length;
  return Math.round((completedVisible / visibleStepCount.value) * 100);
});

/** Position shown in the "Step X of Y" counter. */
const displayStepIndex = computed(
  () => visibleSteps.value.find((s) => s.number === activeStep.value)?.displayNumber ?? activeStep.value,
);

/**
 * Backend-authoritative settlement breakdown. Also read directly below by the
 * doGenerate() analytics payload — kept separate from useSettlementSummaryCardProps'
 * own internal copy so a display-only change there can never silently affect
 * what gets sent to analytics.
 */
const breakdown = computed((): ComputedBreakdown | null => {
  return settlementStore.data?.computed ?? null;
});

const lienTotal = computed(() => settlementStore.liens.totalLiens);

/** Every prop SettlementSummaryCard needs, computed in one place — see useSettlementSummaryCardProps.ts. */
const summaryCardProps = useSettlementSummaryCardProps(settingsForm, allCompletedSteps);

// ── Step 4: Liens ────────────────────────────────────────
/** Handler for SettlementLienStepFF — step 4 confirmed via from-table path. */
function onLienStepConfirmed() {
  trackSettlement(
    TrackingCategory.SETTLEMENT_WIZARD,
    TrackingAction.WIZARD_STEP_COMPLETED,
    {
      pageType: 'settlement-wizard',
      stepNumber: 4,
      stepName: 'Review Liens',
      totalStepsAvailable: isMapPaymentsDisabled.value ? 5 : 6,
      durationSeconds: stepDurationSeconds(4),
      feeMethod: settingsForm.feeMethod || 'not_configured',
      expenseArrangement: settingsForm.expenseArrangement || 'not_configured',
    },
  );
  const next = nextStepAfter(4);
  if (next) activeStep.value = next;
}

async function handlePaymentStepCompleted() {
  const next = nextStepAfter(5);
  if (next) activeStep.value = next;
}

// ── Navigation ───────────────────────────────────────────
/** Navigate to Firm Settings → Trust Accounting setup page. */
function goToTrustSettings() {
  props.state?.go('settings.company.trust');
}

/** Navigate to matter billing → trust to connect a trust sub-account. */
function goToMatterTrust() {
  const matter = clientMatterStore.matter as any;
  props.state?.go('matters-next.client.matter.billing.trust', {
    id: matter?.client?.id,
    matterId: matterId.value,
  });
}

// ── Step Navigation ──────────────────────────────────────

/** Called when the user clicks or keyboard-activates a step in the rail. */
function handleRailStepClick(stepNumber: number) {
  if (settlementStore.isGenerated) return;
  if (stepNumber === MAP_PAYMENTS_STEP && isMapPaymentsDisabled.value) return;
  goToStep(stepNumber);
}

function isStepCompleted(stepNumber: number) {
  return (
    settlementStore.completedSteps.includes(stepNumber) ||
    localCompletedSteps.value.includes(stepNumber)
  );
}

function canNavigateToStep(stepNumber: number) {
  // Once settlement is generated, navigation is locked — user must unlock to edit
  if (settlementStore.isGenerated) return false;
  // If trust is not connected at the matter level, only allow step 1
  if (!isTrustConnected.value) return stepNumber === 1;
  if (allCompletedSteps.value.includes(stepNumber)) return true;
  return stepNumber === settlementStore.currentStep;
}

function goToStep(stepNumber: number) {
  // If trust is not connected at the matter level, always force step 1
  if (!isTrustConnected.value) {
    activeStep.value = 1;
    return;
  }
  if (!canNavigateToStep(stepNumber)) return;
  activeStep.value = stepNumber;
}

function prevStepBefore(stepNumber: number) {
  const steps = stepDefinitions.map((s) => s.number);
  const idx = steps.indexOf(stepNumber);
  let prev = idx > 0 ? idx - 1 : 0;
  // DD-24: skip Map Payments step when not applicable
  if (steps[prev] === MAP_PAYMENTS_STEP && isMapPaymentsDisabled.value && prev > 0) prev -= 1;
  return steps[prev];
}

function nextStepAfter(stepNumber: number) {
  const steps = stepDefinitions.map((s) => s.number);
  const idx = steps.indexOf(stepNumber);
  if (idx < 0 || idx >= steps.length - 1) return null;
  let next = idx + 1;
  // DD-24: skip Map Payments step when not applicable
  if (
    steps[next] === MAP_PAYMENTS_STEP &&
    isMapPaymentsDisabled.value &&
    next < steps.length - 1
  )
    next += 1;
  return steps[next];
}

// ── Step 1: Confirm Recovery ─────────────────────────────
// Flow: createSettlement (with settings) → confirmDeposit (freeze deposits + calc)
async function confirmRecovery() {
  step1ValidationError.value = '';
  if (!settlementStore.canConfirmStep1) {
    step1ValidationError.value =
      'Please select at least one deposit before confirming.';
    return;
  }
  if (!matterId.value) return;
  try {
    // Only create here if the Settings panel hasn't already done so (early-save) —
    // avoids a redundant create call once a settlement already exists for this matter.
    if (!settlementStore.settlementId) {
      await settlementStore.createSettlement(matterId.value, {
        feePercentage: settingsForm.feePercentage,
        feeMethod: settingsForm.feeMethod,
        expenseArrangement: settingsForm.expenseArrangement,
        roundToNearestDollar: settingsForm.roundToNearestDollar,
        settlementDate: settingsForm.settlementDate,
      });
    }
    // Capture before confirm — once confirmDeposit returns, data.recoveryAmount is set
    const isFirstEntry = !settlementStore.data?.recoveryAmount;
    // Same reason, and it must come from persisted state — see the Wizard Started
    // guard below. completedSteps reads the server's steps array, falling back to
    // recoveryAmount, so it survives a reload the way a local flag cannot.
    const step1AlreadyConfirmed = settlementStore.completedSteps.includes(1);
    // Freeze selected deposits and run calculation engine on the backend
    await settlementStore.confirmDeposit();

    // Event 8: Recovery Amount Entered — deposit selection confirmed
    trackSettlement(
      TrackingCategory.SETTLEMENT_TAB,
      TrackingAction.RECOVERY_AMOUNT_ENTERED,
      {
        pageType: 'settlement-tab',
        recoveryAmount: settlementStore.recoveryAmount,
        isFirstEntry,
      },
    );

    // Page-lifetime timer feeding totalWizardDurationSeconds. Deliberately kept
    // separate from the guard below: it is not an idempotency signal, and sc-46997
    // replaces it with persisted step timestamps.
    if (!wizardStartedAt) {
      wizardStartedAt = Date.now();
    }

    // Event 3: Wizard Started — only on the first Step 1 confirm for this settlement.
    // Guarded on persisted step state rather than a page-lifetime flag: the flag dies
    // with the page, so re-confirming Step 1 after a reload re-fired this event and
    // inflated any unique-matter denominator (SETTLE:OM-002, OM-006). Unlock & Reset
    // clears the step rows server-side, so a genuinely new run still fires. sc-47003.
    if (!step1AlreadyConfirmed) {
      trackSettlement(
        TrackingCategory.SETTLEMENT_WIZARD,
        TrackingAction.WIZARD_STARTED,
        {
          pageType: 'settlement-wizard',
          depositCount: settlementStore.deposits.selectedIds.length,
          recoveryAmount: settlementStore.recoveryAmount,
          feePercent: settingsForm.feePercentage,
          feeMethod: settingsForm.feeMethod || 'not_configured',
          expenseArrangement:
            settingsForm.expenseArrangement || 'not_configured',
        },
      );
    }

    // Event 2: Wizard Step Completed — Step 1
    trackSettlement(
      TrackingCategory.SETTLEMENT_WIZARD,
      TrackingAction.WIZARD_STEP_COMPLETED,
      {
        pageType: 'settlement-wizard',
        stepNumber: 1,
        stepName: 'Enter Settlement',
        totalStepsAvailable: isMapPaymentsDisabled.value ? 5 : 6,
        durationSeconds: stepDurationSeconds(1),
        feeMethod: settingsForm.feeMethod || 'not_configured',
        expenseArrangement: settingsForm.expenseArrangement || 'not_configured',
      },
    );

    // Advance stepper before loading expenses — loadExpenses is a prefetch for Step 2
    // and must not block the step transition if it fails
    const next = nextStepAfter(1);
    if (next) activeStep.value = next;
    // Load expenses for Step 2 (fire-and-forget; errors handled by store)
    settlementStore.loadExpenses(matterId.value).catch(() => {});
  } catch {
    // Error handled by store
  }
}

// ── Step 2: Confirm Expenses ─────────────────────────────
async function confirmExpenses() {
  if (!settlementStore.settlementId) return;
  try {
    await settlementStore.confirmStep2();

    // Event 2: Wizard Step Completed — Step 2
    trackSettlement(
      TrackingCategory.SETTLEMENT_WIZARD,
      TrackingAction.WIZARD_STEP_COMPLETED,
      {
        pageType: 'settlement-wizard',
        stepNumber: 2,
        stepName: 'Confirm Expenses',
        totalStepsAvailable: isMapPaymentsDisabled.value ? 5 : 6,
        durationSeconds: stepDurationSeconds(2),
        feeMethod: settingsForm.feeMethod || 'not_configured',
        expenseArrangement: settingsForm.expenseArrangement || 'not_configured',
      },
    );

    const next = nextStepAfter(2);
    if (next) activeStep.value = next;
    // Load attorney fee data for Step 3 (fire-and-forget; errors handled by store)
    settlementStore.loadAttorneyFee().catch(() => {});
  } catch {
    // Error handled by store
  }
}

/** Navigate to the Expenses page, pre-filtered to this matter.
 *  Pass expenseId to deep-link directly into a specific expense edit modal. */
function navigateToExpenses(expenseId?: string | null) {
  const matter = clientMatterStore.matter as any;
  props.state?.go('expenses.list', {
    matterId: matter?.id ?? matterId.value,
    matterName: matter?.name ?? '',
    clientId: matter?.client?.id ?? '',
    clientName: matter?.client?.name ?? '',
    ...(expenseId ? { expenseId } : {}),
  });
}

function navigateToFixedFees() {
  props.state?.go('fixedfees');
}

// ── Step Completion (shell — actual logic in Stories 2–5) ─
function completeStep(stepNumber: number) {
  if (!localCompletedSteps.value.includes(stepNumber)) {
    localCompletedSteps.value.push(stepNumber);
  }
  const next = nextStepAfter(stepNumber);
  if (next) {
    activeStep.value = next;
  }
}

// ── Step 3: Fee Breakdown Computeds ──────────────────────
const expenseLabel = computed(() =>
  settingsForm.expenseArrangement === EXPENSE_ARRANGEMENT.CLIENT
    ? 'Case Costs'
    : 'Expenses Advanced on Behalf of Client',
);

const feeBreakdown = computed(() => {
  const pct = settingsForm.feePercentage ?? 0;
  const pctLabel = `${pct}%`;
  const fm = settingsForm.feeMethod;
  const ea = settingsForm.expenseArrangement;
  const summary = settlementStore.attorneyFee.summary;

  let variant = 'gross';
  if (ea === EXPENSE_ARRANGEMENT.ABSORBED) {
    variant = fm === FEE_METHOD.NET ? 'absorbed-net' : 'absorbed-gross';
  } else if (fm === FEE_METHOD.NET) {
    variant = 'net';
  }

  return { summary, pctLabel, variant };
});

const backendAttorneyFeeAmount = computed(() => {
  return settlementStore.attorneyFee.calculatedFee;
});

/** Amount of the existing settlement fee charge (used in mismatch banner). */
const existingSettlementFeeAmount = computed(() => {
  const fee = settlementStore.attorneyFee.existingFees.find(
    (f) => f.isSettlementFee,
  );
  return fee?.amount ?? 0;
});

/** True once Step 3 has actually been confirmed with co-counsel recipients —
 *  calculatedShareAmount is only ever populated by the confirmed settlement
 *  snapshot (loadAttorneyFee), never by the matter-defaults auto-populate. */
const isRecipientsConfirmed = computed(() =>
  settlementStore.attorneyFee.feeRecipients.some(
    (r) => r.calculatedShareAmount != null,
  ),
);

// ── Step 3: Record Attorney Fee ──────────────────────────
async function handleRecordFee(payload?: {
  description?: string;
  feeDate?: string;
}) {
  const feeAmount = settlementStore.attorneyFee.calculatedFee;
  if (feeAmount == null) return;
  try {
    await settlementStore.recordFee({
      amount: feeAmount,
      description: payload?.description,
      feeDate: payload?.feeDate,
    });
    handleSuccess('Attorney fee recorded');
  } catch {
    // Error handled by store
  }
}

function handleSelectFee(chargeId: string) {
  settlementStore.selectFee(chargeId);
}

/** Auto-populate on Step 3 mount when no recipients are loaded yet (P4.5). */
function handleLoadFeeRecipients() {
  settlementStore.loadMatterFeeRecipients();
}

function handleLoadCoCounselExpenses() {
  settlementStore.loadCoCounselExpenses();
}

function handleUpdateCoCounselExpense({ vendorId, amount }: { vendorId: string; amount: number }) {
  settlementStore.updateCoCounselExpenseAmount(vendorId, amount);
}

/** Keeps store state in sync with in-progress percentage edits. */
function handleUpdateFeeRecipients(recipients: FeeRecipient[]) {
  settlementStore.setFeeRecipients(recipients);
}

/** Fires just before auto-confirm-fee on the same click — see handleAutoConfirmFee. */
function handleConfirmFeeRecipients(recipients: FeeRecipient[]) {
  settlementStore.setFeeRecipients(recipients);
}

/**
 * Redesign (SC-45767): single-action confirm — one atomic
 * POST /attorney-fee/confirm creates/updates the charge (backend always uses
 * its own AttorneyFeeSnapshot for the amount) and links it, replacing the old
 * separate record-then-confirm round trip. Called by the redesign
 * "Confirm Attorney Fee →" button.
 */
async function handleAutoConfirmFee(payload?: { description?: string; feeDate?: string }) {
  if (settlementStore.attorneyFee.calculatedFee == null) return;
  try {
    await settlementStore.confirmAttorneyFeeRedesign({
      feeChargeId: settlementStore.attorneyFee.selectedChargeId ?? undefined,
      feeDescription: payload?.description,
      feeDate: payload?.feeDate,
      // handleConfirmFeeRecipients (confirm-fee-recipients) fires first and syncs
      // this into store state, so it's already current by the time this reads it.
      feeRecipients: settlementStore.attorneyFee.feeRecipients,
    });
    await afterAttorneyFeeConfirmed();
  } catch {
    // Errors are surfaced by the store; afterAttorneyFeeConfirmed is not reached on failure.
  }
}

async function confirmStep3() {
  try {
    await settlementStore.confirmAttorneyFee();
    await afterAttorneyFeeConfirmed();
  } catch {
    // Error handled by store
  }
}

/** Shared post-confirm flow for Step 3, regardless of legacy vs redesign call path. */
async function afterAttorneyFeeConfirmed() {
  // Event 2: Wizard Step Completed — Step 3
  trackSettlement(
    TrackingCategory.SETTLEMENT_WIZARD,
    TrackingAction.WIZARD_STEP_COMPLETED,
    {
      pageType: 'settlement-wizard',
      stepNumber: 3,
      stepName: 'Record Attorney Fee',
      totalStepsAvailable: isMapPaymentsDisabled.value ? 5 : 6,
      durationSeconds: stepDurationSeconds(3),
      feeMethod: settingsForm.feeMethod || 'not_configured',
      expenseArrangement: settingsForm.expenseArrangement || 'not_configured',
    },
  );

  const next = nextStepAfter(3);
  if (next) activeStep.value = next;
  // Load liens for Step 4 (fire-and-forget; errors handled by store)
  if (settlementStore.settlementId) {
    settlementStore.loadLiensFromTable().catch(() => {});
  }
}

// ── Record Deposit (TrustDeposit modal) ──────────────────────
function openTrustDeposit() {
  showDepositDialog.value = true;
}

function closeDepositDialog() {
  showDepositDialog.value = false;
}

// ── Expense Dialog (SC-45761) ─────────────────────────────
function openAddExpense() {
  editingExpenseId.value = null;
  showExpenseDialog.value = true;
}

function openEditExpense(expenseId: string) {
  editingExpenseId.value = expenseId;
  showExpenseDialog.value = true;
}

async function onExpenseSaved(reload: boolean) {
  showExpenseDialog.value = false;
  editingExpenseId.value = null;
  if (reload && matterId.value) {
    await settlementStore.loadExpenses(matterId.value);
  }
}

/**
 * Default deposit filter start date: the matter's open date, falling back to
 * Jan 1 of the current year for legacy matters with no open date. Without this
 * the backend fetches all-time QBO history (the slow path).
 */
function getDefaultDepositStartDate(): string {
  const opened = matterData.value?.opened;
  const openedDate = typeof opened === 'string' ? parseISO(opened) : opened;
  if (openedDate && isValid(openedDate)) {
    return format(openedDate, 'yyyy-MM-dd');
  }
  // Legacy matters with no open date: fall back to Jan 1 of the current year.
  return format(startOfYear(new Date()), 'yyyy-MM-dd');
}

async function onDepositSaved() {
  showDepositDialog.value = false;
  if (matterId.value && !isClientAccountingBlocked.value) {
    // Reuse the active filter so the view doesn't jump back to the default range.
    await settlementStore.loadDeposits(
      matterId.value,
      settlementStore.deposits.filterStartDate ?? getDefaultDepositStartDate(),
      settlementStore.deposits.filterEndDate ?? undefined,
    );
  }
}

/** User widened/changed the deposit date range — re-fetch with the chosen dates. */
async function onDepositDateRangeChange(range: {
  startDate?: string;
  endDate?: string;
}) {
  if (!matterId.value || isClientAccountingBlocked.value) return;
  // Invariant: always send a startDate. If the picker was cleared, fall back to
  // the matter's open date so we never trigger the all-time (slow) QBO query.
  const startDate = range.startDate || getDefaultDepositStartDate();
  await settlementStore.loadDeposits(matterId.value, startDate, range.endDate);
}

// ── Settings ─────────────────────────────────────────────
/** Used by the inline Expense step's "as-of date" picker (Step 2) — a separate,
 *  live-editing workflow from the Settings panel draft below. */
function onExpenseFromDateChange(date: string | null) {
  settlementStore.expenses.expenseFromDate = date;
}

/** New design only: user clicked a sortable expense column header — reload from the backend
 *  in that order rather than re-sorting the already-loaded list locally. */
function onExpenseSort(sort: { orderby: string; order: 'asc' | 'desc' }) {
  if (!matterId.value) return;
  settlementStore.loadExpenses(matterId.value, sort).catch(() => {});
}

/** Returns whether the update actually succeeded — callers that batch this alongside
 *  other saves (see onSettingsSave) need to know before reporting overall success. */
async function onNoExpenseInterestChange(noExpenseInterest: boolean): Promise<boolean> {
  if (!matterId.value) return false;
  const requestedMatterId = matterId.value;
  const previous = !!matterData.value?.billing?.contingency?.noExpenseInterest;
  if (matterData.value?.billing?.contingency) {
    matterData.value.billing.contingency.noExpenseInterest = noExpenseInterest;
  }
  try {
    const updatedMatter = await clientMatterStore.updateMatter({
      id: matterId.value,
      update: {
        billing: {
          ...matterData.value?.billing,
          contingency: {
            ...(matterData.value?.billing?.contingency || {}),
            noExpenseInterest,
          },
        },
      },
    });

    // updateMatter returns null when the active matter changed while request was in flight.
    if (!updatedMatter) {
      if (String(matterId.value) === String(requestedMatterId)) {
        if (matterData.value?.billing?.contingency) {
          matterData.value.billing.contingency.noExpenseInterest = previous;
        }
        handleError('Unable to update expense interest setting');
      }
      return false;
    }

    if (
      String(matterId.value) === String(requestedMatterId) &&
      matterData.value
    ) {
      matterData.value.billing = updatedMatter.billing;
    }
    return true;
  } catch {
    if (matterData.value?.billing?.contingency) {
      matterData.value.billing.contingency.noExpenseInterest = previous;
    }
    handleError('Unable to update expense interest setting');
    return false;
  }
}

/** Case Status is a Matter field, like noExpenseInterest above — not a Settlement-record
 *  field, so it's saved via its own sparse PUT rather than through settlementStore.updateSettings,
 *  and it must never participate in the "will reset completed steps" diff/confirmation. */
async function onCaseStatusChange(newStatus: string | null): Promise<boolean> {
  if (!matterId.value) return false;
  const requestedMatterId = matterId.value;
  const previous = matterData.value?.caseStatus ?? null;
  if (matterData.value) matterData.value.caseStatus = newStatus;
  try {
    const updatedMatter = await clientMatterStore.updateMatter({
      id: matterId.value,
      update: { caseStatus: newStatus == null || newStatus === '' ? '' : newStatus },
    });

    if (!updatedMatter) {
      if (String(matterId.value) === String(requestedMatterId)) {
        if (matterData.value) matterData.value.caseStatus = previous;
        handleError('Unable to update case status');
      }
      return false;
    }

    if (String(matterId.value) === String(requestedMatterId) && matterData.value) {
      matterData.value.caseStatus = updatedMatter.caseStatus;
    }
    return true;
  } catch {
    if (matterData.value) matterData.value.caseStatus = previous;
    handleError('Unable to update case status');
    return false;
  }
}

/** Diff the Settings panel's saved draft against the last-saved settingsForm — only
 *  changed keys are returned. Diffed here (against live settingsForm), not inside the
 *  modal, so a retry after a partial save failure re-diffs against whatever already
 *  persisted rather than a stale snapshot from whenever the panel happened to open. */
function computeSettingsDiff(draft: {
  feePercentage: number | null;
  feeMethod: FeeMethod | null;
  expenseArrangement: ExpenseArrangement | null;
  settlementDate: string | null;
}) {
  const changed: Partial<{
    feePercentage: number | null;
    feeMethod: FeeMethod | null;
    expenseArrangement: ExpenseArrangement | null;
    settlementDate: string | null;
  }> = {};
  if (draft.feePercentage !== settingsForm.feePercentage) changed.feePercentage = draft.feePercentage;
  if (draft.feeMethod !== settingsForm.feeMethod) changed.feeMethod = draft.feeMethod;
  if (draft.expenseArrangement !== settingsForm.expenseArrangement) changed.expenseArrangement = draft.expenseArrangement;
  if (draft.settlementDate !== settingsForm.settlementDate) changed.settlementDate = draft.settlementDate;
  return changed;
}

/**
 * Fire one SETTINGS_CHANGED analytics event per key whose value actually changed.
 * `newValues` should hold the real (untransformed) draft values — not the PATCH
 * payload, which may substitute `undefined` for `null` for sparse-update semantics.
 */
function trackBatchedSettingsChanged(
  keys: string[],
  oldValues: Record<string, unknown>,
  newValues: Record<string, unknown>,
  triggeredReset: boolean,
) {
  for (const key of keys) {
    const newValue = newValues[key];
    if (newValue === oldValues[key]) continue;
    trackSettlement(TrackingCategory.SETTLEMENT_TAB, TrackingAction.SETTINGS_CHANGED, {
      pageType: 'settlement-tab',
      settingChanged: key,
      oldValue: oldValues[key],
      newValue,
      // A reset-triggered save only fires when hasCompletedSteps was true at the moment
      // the user made the change — but by the time this runs, the reset has already
      // cleared completedSteps, so currentStep would misreport the wizard as not-in-progress.
      wizardInProgress: triggeredReset ? true : settlementStore.currentStep > 1,
      triggeredReset,
    });
  }
}

async function onSettingsSave(payload: {
  settings: {
    feePercentage: number | null;
    feeMethod: FeeMethod | null;
    expenseArrangement: ExpenseArrangement | null;
    settlementDate: string | null;
  };
  noExpenseInterest: boolean;
  expenseFromDate: string | null;
  caseStatus: string | null;
}) {
  if (!matterId.value) {
    settingsModalRef.value?.close();
    return;
  }
  const requestedMatterId = matterId.value;
  const diff = computeSettingsDiff(payload.settings);
  const noExpenseInterestChanged = payload.noExpenseInterest !== matterNoExpenseInterest.value;
  const expenseFromDateChanged = payload.expenseFromDate !== settlementStore.expenses.expenseFromDate;
  const caseStatusChanged = payload.caseStatus !== (matterData.value?.caseStatus ?? null);

  // Settlement not created yet — create it now (early-save) so settings persist from the
  // very first save instead of waiting for Step 1 confirm. POST is idempotent, so Step 1's
  // later createSettlement() call just re-confirms the same settings.
  //
  // Case Status is a Matter field with no effect on settlement math — it never triggers the
  // "resets completed steps" dialog by itself, and a brand-new settlement has no completed
  // steps to reset anyway, so it always saves immediately here, same as below.
  if (!settlementStore.settlementId) {
    const caseStatusSaved = caseStatusChanged
      ? await onCaseStatusChange(payload.caseStatus)
      : true;
    if (String(matterId.value) !== String(requestedMatterId)) return;

    Object.assign(settingsForm, payload.settings);
    if (expenseFromDateChanged) settlementStore.expenses.expenseFromDate = payload.expenseFromDate;
    await settlementStore.createSettlement(matterId.value, {
      feePercentage: settingsForm.feePercentage,
      feeMethod: settingsForm.feeMethod,
      expenseArrangement: settingsForm.expenseArrangement,
      roundToNearestDollar: settingsForm.roundToNearestDollar,
      expenseFromDate: settlementStore.expenses.expenseFromDate,
      settlementDate: settingsForm.settlementDate,
    });
    if (String(matterId.value) !== String(requestedMatterId)) return;
    const noExpenseInterestSaved = noExpenseInterestChanged
      ? await onNoExpenseInterestChange(payload.noExpenseInterest)
      : true;
    if (String(matterId.value) !== String(requestedMatterId)) return;
    if (noExpenseInterestSaved && caseStatusSaved) settingsModalRef.value?.close();
    return;
  }

  // While the user is on the Expense step itself, editing the as-of date is just
  // previewing interest for a not-yet-(re)confirmed Step 2 — "Confirm Interest"/
  // "Confirm Expenses" is the normal, non-destructive way to lock it in, so it
  // should never pop the reset dialog from here. Once Step 2 is confirmed, an edit
  // made from anywhere else can invalidate that confirmed total, so it should.
  const expenseFromDateNeedsReset =
    expenseFromDateChanged && activeStep.value !== 2 && settlementStore.expenses.isConfirmed;

  const hasResetTriggeringChange =
    diff.feeMethod !== undefined || diff.expenseArrangement !== undefined || diff.feePercentage !== undefined
    || diff.settlementDate !== undefined || noExpenseInterestChanged || expenseFromDateNeedsReset;
  if (settlementStore.hasCompletedSteps && hasResetTriggeringChange) {
    pendingSettings.value = {
      feeMethod: diff.feeMethod ?? settingsForm.feeMethod,
      expenseArrangement: diff.expenseArrangement ?? settingsForm.expenseArrangement,
      feePercentage: 'feePercentage' in diff ? diff.feePercentage : settingsForm.feePercentage,
      settlementDate: diff.settlementDate ?? settingsForm.settlementDate,
    };
    pendingNoExpenseInterest.value = noExpenseInterestChanged ? payload.noExpenseInterest : null;
    pendingExpenseFromDate.value = expenseFromDateNeedsReset ? payload.expenseFromDate : undefined;
    // Don't save Case Status yet — the reset dialog is about to ask the user whether to
    // proceed at all. Committing it now would mean canceling that dialog (or the whole
    // panel) leaves this one field changed behind their back. Deferred to
    // confirmSettingsChange(), applied only if they actually confirm.
    pendingCaseStatus.value = caseStatusChanged ? payload.caseStatus : undefined;
    showSettingsResetDialog.value = true; // panel stays open until the user resolves this dialog
    return;
  }

  // No reset confirmation needed — Case Status saves immediately, same as the early-save
  // branch above, since there's no pending decision here for it to wait on. Wrapped in the
  // same savingSettings guard as everything else below so Save/Cancel/X are correctly
  // disabled for its whole duration too, not just once the settlement-record PATCH starts.
  savingSettings.value = true;
  try {
    const caseStatusSaved = caseStatusChanged
      ? await onCaseStatusChange(payload.caseStatus)
      : true;
    if (String(matterId.value) !== String(requestedMatterId)) return;

    // The interest as-of date is sent in the same PATCH so a Settings-panel edit persists
    // to the backend (not just the client store) and survives reload.
    const patch: UpdateSettlementRequest = { ...diff };
    if (expenseFromDateChanged) patch.expenseFromDate = payload.expenseFromDate;
    if (Object.keys(patch).length > 0) {
      const oldValues = {
        feePercentage: settlementStore.data?.feePercentage ?? null,
        feeMethod: settlementStore.data?.feeMethod ?? null,
        expenseArrangement: settlementStore.data?.expenseArrangement ?? null,
        settlementDate: settlementStore.data?.settlementDate ?? null,
      };
      await settlementStore.updateSettings(matterId.value, patch);
      syncSettingsFromStore();
      await refreshAttorneyFeeIfAvailable();
      // Track only the settings-form fields (expenseFromDate is tracked via Step 2 confirm).
      trackBatchedSettingsChanged(Object.keys(diff), oldValues, diff, false);
    }
    if (expenseFromDateChanged) settlementStore.expenses.expenseFromDate = payload.expenseFromDate;
    const noExpenseInterestSaved = noExpenseInterestChanged
      ? await onNoExpenseInterestChange(payload.noExpenseInterest)
      : true;

    // The user may have navigated to a different matter while this save was in flight —
    // don't report success or close a panel that no longer belongs to what's on screen.
    if (String(matterId.value) !== String(requestedMatterId)) return;

    if (noExpenseInterestSaved && caseStatusSaved) {
      handleSuccess('Settlement settings saved');
      settingsModalRef.value?.close();
    }
    // else: the settlement-level fields above are already saved; only the expense-interest
    // toggle failed and was rolled back internally. onNoExpenseInterestChange already
    // surfaced its own error toast — leave the panel open with the draft untouched so
    // the diff against the rolled-back value still shows a pending change the user can retry.
  } catch (err: any) {
    handleError(err?.response?.data?.message || 'Unable to save settlement settings');
    // leave the panel open with the draft intact so the user can retry without re-entering everything
  } finally {
    savingSettings.value = false;
  }
}

// Refresh data when the tab is re-entered (Vue keep-alive or route reuse)

/** SETTLE:BR-V4-1 — resolve the client's matter count on load, not at statement
 *  generation, so the Statement Generated emit never waits on a network call. Analytics
 *  only: a failed count reports null rather than surfacing an error the user can't act on. */
function loadClientMatterCount(clientId: string | undefined) {
  clientMatterCount.value = null;
  if (!clientId) return;
  getClientMatterCount(clientId)
    .then((count) => {
      // Drop a count for a client the user has already navigated away from.
      if (matterData.value?.client?.id === clientId) {
        clientMatterCount.value = count;
      }
    })
    // Already null for this load, and a newer load's count must not be cleared by this one.
    .catch(() => {});
}

// Shared logic for loading/refreshing settlement data
async function loadSettlementData() {
  const requestedMatterId = matterId.value;
  if (!requestedMatterId) {
    uiState.loadSucceeded = false;
    uiState.loading = false;
    handleError('No matter ID provided');
    return;
  }
  uiState.loadSucceeded = false;
  uiState.loading = true;
  try {
    await refreshUserAndCompany();

    // Load matter and settlement in parallel — matter provides contingency fee default
    // Force-refresh matter so trust account changes are reflected without a full reload
    const [matter] = await Promise.all([
      clientMatterStore.getMatterById(requestedMatterId, true),
      settlementStore.load(requestedMatterId),
    ]);

    // Ignore stale responses if user switched matters while requests were in flight.
    if (String(matterId.value) !== String(requestedMatterId)) {
      return;
    }

    matterData.value = matter;
    loadClientMatterCount(matter?.client?.id);
    loadCaseStatusOptions();

    syncSettingsFromStore();

    // If the settlement has no saved fee percentage yet, seed the form field
    // from the matter's contingency rate. This is purely a form default —
    // it will be persisted when the user confirms Step 1 (POST) or blurs the field (PATCH).
    if (
      settingsForm.feePercentage == null &&
      matter?.contingencyFeePercentage != null
    ) {
      settingsForm.feePercentage = matter.contingencyFeePercentage;
    }

    settingsForm.settlementDate =
      (settlementStore.data as any)?.settlementDate ?? null;
    // Initial load: default to the matter's open date so the backend doesn't
    // fetch all-time QBO history. No endDate — return everything up to today.
    if (!isClientAccountingBlocked.value) {
      await settlementStore.loadDeposits(
        requestedMatterId,
        getDefaultDepositStartDate(),
      );
    }

    // Load expenses if Step 1 is already completed (resuming wizard).
    // Errors are handled by the store — don't let them block the outer catch.
    if (settlementStore.completedSteps.includes(1)) {
      await settlementStore.loadExpenses(requestedMatterId).catch(() => {});
    }

    // Load attorney fee data if Step 2 is already completed (resuming wizard).
    if (settlementStore.completedSteps.includes(2)) {
      settlementStore.loadAttorneyFee().catch(() => {});
    }

    // Load liens eagerly — Step 4 is always visible regardless of expense arrangement.
    // Requires settlementId — only load if a settlement already exists (resuming wizard).
    if (settlementStore.completedSteps.includes(3)) {
      settlementStore.loadLiensFromTable().catch(() => {});
    }

    // Apply the same Step 5 skip logic used in the currentStep watcher
    const step = settlementStore.currentStep;
    if (step === 5 && isMapPaymentsDisabled.value) {
      activeStep.value = 6;
    } else {
      activeStep.value = step;
    }

    // Load trust balance in parallel if settlement is already generated (post-settlement panel).
    if (settlementStore.isGenerated) {
      settlementStore.loadTrustBalance(matterId.value).catch(() => {});
    }

    uiState.loadSucceeded = true;
  } catch (err: any) {
    handleError(
      err?.response?.data?.message || 'Unable to load settlement data',
    );
  } finally {
    uiState.loading = false;
  }

  // Event 1: Page Viewed — fire after load regardless of success
  trackSettlement(TrackingCategory.SETTLEMENT_TAB, TrackingAction.PAGE_VIEWED, {
    pageType: 'settlement-tab',
    hasRecoveryAmount: !!settlementStore.data?.recoveryAmount,
    wizardStep: settlementStore.currentStep || 0,
    feeMethod: settingsForm.feeMethod || 'not_configured',
    expenseArrangement: settingsForm.expenseArrangement || 'not_configured',
  });
}

onMounted(loadSettlementData);
onActivated(loadSettlementData);

// Fetch the authenticated blob URL and trust balance when the statement PDF becomes available
watch(
  () => settlementStore.isGenerated,
  (ready) => {
    if (ready) {
      settlementStore.fetchStatementBlobUrl();
      if (matterId.value) {
        settlementStore.loadTrustBalance(matterId.value).catch(() => {});
      }
    }
  },
  { immediate: true },
);

onBeforeUnmount(() => {
  settlementStore.revokeStatementBlobUrl();
  settlementStore.revokePreviewBlobUrl();
});

async function saveSettingsWithReset() {
  if (!matterId.value) return;
  const oldValues = {
    feeMethod: settlementStore.data?.feeMethod ?? null,
    expenseArrangement: settlementStore.data?.expenseArrangement ?? null,
    feePercentage: settlementStore.data?.feePercentage ?? null,
    settlementDate: settlementStore.data?.settlementDate ?? null,
  };
  const diff: UpdateSettlementRequest = {
    feePercentage: pendingSettings.value!.feePercentage,
    feeMethod: pendingSettings.value!.feeMethod,
    expenseArrangement: pendingSettings.value!.expenseArrangement,
    settlementDate: pendingSettings.value!.settlementDate,
  };
  // Persist the interest as-of date in the reset payload too when it changed.
  if (pendingExpenseFromDate.value !== undefined) {
    diff.expenseFromDate = pendingExpenseFromDate.value;
  }
  await settlementStore.resetAndUpdateSettings(matterId.value, diff);
  syncSettingsFromStore();
  await refreshAttorneyFeeIfAvailable();
  trackBatchedSettingsChanged(Object.keys(oldValues), oldValues, pendingSettings.value!, true);
}

async function confirmSettingsChange() {
  showSettingsResetDialog.value = false;
  const requestedMatterId = matterId.value;
  try {
    await saveSettingsWithReset();
    const noExpenseInterestSaved =
      pendingNoExpenseInterest.value !== null
        ? await onNoExpenseInterestChange(pendingNoExpenseInterest.value)
        : true;
    const caseStatusSaved =
      pendingCaseStatus.value !== undefined
        ? await onCaseStatusChange(pendingCaseStatus.value)
        : true;
    if (pendingExpenseFromDate.value !== undefined) {
      settlementStore.expenses.expenseFromDate = pendingExpenseFromDate.value;
    }

    // The user may have navigated to a different matter while this was in flight —
    // don't mutate wizard/panel state for whatever matter is now on screen.
    if (String(matterId.value) !== String(requestedMatterId)) return;

    activeStep.value = 1;
    // The settings reset/update itself succeeded either way — only skip closing the panel
    // if a separate deferred update (expense-interest or case status) failed, so the user
    // can see and retry it.
    if (noExpenseInterestSaved && caseStatusSaved) settingsModalRef.value?.close();
  } catch (err: any) {
    handleError(
      err?.response?.data?.message || 'Unable to save settlement settings',
    );
    // Revert form to store values on failure — leave the panel open, draft intact, so
    // the user can retry rather than lose their edits.
    syncSettingsFromStore();
  } finally {
    pendingSettings.value = null;
    pendingNoExpenseInterest.value = null;
    pendingExpenseFromDate.value = undefined;
    pendingCaseStatus.value = undefined;
  }
}

function revertSettingsChange() {
  showSettingsResetDialog.value = false;
  syncSettingsFromStore();
  pendingSettings.value = null;
  pendingNoExpenseInterest.value = null;
  pendingExpenseFromDate.value = undefined;
  // Discarded, never applied — this is what actually fixes the bug: declining the reset
  // means the deferred Case Status edit is simply never sent to the backend, matching
  // every other field in this same pending batch.
  pendingCaseStatus.value = undefined;
  // The user only declined the "reset completed steps" side effect, not the whole edit
  // session — leave the Settings panel open with their draft untouched.
}

async function onRoundingChange(value: boolean) {
  const previousValue = settingsForm.roundToNearestDollar;
  settingsForm.roundToNearestDollar = value;

  if (!matterId.value) return;
  // Before settlement exists, keep changes local — they'll be sent with POST on confirm
  if (!settlementStore.settlementId) return;
  try {
    await settlementStore.updateSettings(matterId.value, {
      roundToNearestDollar: value,
    });
    syncSettingsFromStore();
    await refreshAttorneyFeeIfAvailable();

    // Event 7: Settings Changed (roundToDollar)
    trackSettlement(
      TrackingCategory.SETTLEMENT_TAB,
      TrackingAction.SETTINGS_CHANGED,
      {
        pageType: 'settlement-tab',
        settingChanged: 'roundToDollar',
        oldValue: previousValue,
        newValue: value,
        wizardInProgress: settlementStore.currentStep > 1,
        triggeredReset: false,
      },
    );
  } catch {
    settingsForm.roundToNearestDollar = previousValue;
  }
}

async function confirmUnlock() {
  showUnlockDialog.value = false;
  if (!matterId.value) return;
  // Capture state before reset for analytics
  const previousStepCompleted = settlementStore.completedSteps.length
    ? Math.max(...settlementStore.completedSteps)
    : 0;
  const hadStatement = !!settlementStore.data?.blobPath;
  const hadInvoice = ['invoiced', 'paidlocked'].includes(
    settlementStore.data?.status?.toLowerCase() ?? '',
  );
  try {
    await settlementStore.unlock(matterId.value);

    // Event 6: Settlement Unlocked
    trackSettlement(
      TrackingCategory.SETTLEMENT_TAB,
      TrackingAction.SETTLEMENT_UNLOCKED,
      {
        pageType: 'settlement-tab',
        previousStepCompleted,
        hadStatement,
        hadInvoice,
      },
    );

    // Reset wizard timer so next generation measures fresh
    wizardStartedAt = null;
    stepViewedAt.clear();

    activeStep.value = 1;
    // Seed step 1 explicitly: the watcher only fires when activeStep actually changes,
    // so resetting while already on step 1 would leave it with no view time and emit a
    // null duration on the next confirm.
    stepViewedAt.set(1, Date.now());
  } catch {
    // Error handled by store
  }
}

async function handleGenerate() {
  if (!matterId.value) return;
  // Intercept: show the stage update dialog before generating (only when V2 is enabled)
  if (isSettlementV2Enabled.value && !stageUpdated.value) {
    showUpdateStageDialog.value = true;
    return;
  }
  await doGenerate();
}

async function doGenerate() {
  if (!matterId.value) return;
  try {
    await settlementStore.generate(matterId.value);

    // SETTLEUX:EV-004 — Step 6 completion. Steps 1-5 each emit this on confirm; step 6
    // had no equivalent, so wizard completion could not be measured symmetrically and
    // Statement Generated had to stand in as a proxy. Emitted from doGenerate() and not
    // from the store action so the reprint path (which calls generate() directly) does
    // not re-report a completion that already happened.
    trackSettlement(
      TrackingCategory.SETTLEMENT_WIZARD,
      TrackingAction.WIZARD_STEP_COMPLETED,
      {
        pageType: 'settlement-wizard',
        stepNumber: 6,
        stepName: 'Generate Statement',
        totalStepsAvailable: isMapPaymentsDisabled.value ? 5 : 6,
        durationSeconds: stepDurationSeconds(6),
        feeMethod: settingsForm.feeMethod || 'not_configured',
        expenseArrangement: settingsForm.expenseArrangement || 'not_configured',
      },
    );

    // Event 4: Statement Generated
    const totalWizardDurationSeconds =
      wizardStartedAt != null
        ? Math.round((Date.now() - wizardStartedAt) / 1000)
        : null;
    trackSettlement(
      TrackingCategory.SETTLEMENT_WIZARD,
      TrackingAction.STATEMENT_GENERATED,
      {
        pageType: 'settlement-wizard',
        recoveryAmount: settlementStore.data?.recoveryAmount ?? null,
        feeAmount: breakdown.value?.fee ?? null,
        expenseTotal: breakdown.value?.totalExpenses ?? null,
        lienTotal: breakdown.value?.lienTotal ?? null,
        netProceeds: breakdown.value?.clientNet ?? null,
        feeMethod: settingsForm.feeMethod || 'not_configured',
        expenseArrangement: settingsForm.expenseArrangement || 'not_configured',
        variant: breakdown.value?.calculationVariant ?? null,
        totalWizardDurationSeconds,
        stepCount: allCompletedSteps.value.length,
        lienCount: settlementStore.lienCount,
        totalLienAmount: lienTotal.value ?? 0,
        lienSource: 'liens_tab',
        accountingMode: isClientLevelBilling.value ? 'Client' : 'Matter',
        matterCount: clientMatterCount.value,
      },
    );
  } catch {
    // Error handled by store
  }
}

async function handleRetryPolling() {
  if (!matterId.value) return;
  try {
    await settlementStore.retryPolling(matterId.value);
  } catch {
    // Error handled by store
  }
}

async function handleReprint() {
  if (!matterId.value) return;
  try {
    // Regenerate: POST statement + poll for new blobPath
    settlementStore.revokeStatementBlobUrl();
    await settlementStore.generate(matterId.value);
    // Once polling completes, fetch and download the new PDF
    const url = await settlementStore.fetchStatementBlobUrl();
    if (url) {
      // Event 5: Statement Downloaded
      const generatedAt = settlementStore.statement.generatedAt;
      trackSettlement(
        TrackingCategory.SETTLEMENT_TAB,
        TrackingAction.STATEMENT_DOWNLOADED,
        {
          pageType: 'settlement-tab',
          secondsSinceGeneration: generatedAt
            ? Math.round((Date.now() - new Date(generatedAt).getTime()) / 1000)
            : null,
        },
      );

      const a = document.createElement('a');
      a.href = url;
      a.download = 'settlement-statement.pdf';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    }
  } catch {
    // Error handled by store
  }
}

async function handleRetryPreview() {
  await settlementStore.retryPreview();
}

function handleStatementDownload() {
  // Event 5: Statement Downloaded
  const generatedAt = settlementStore.statement.generatedAt;
  trackSettlement(
    TrackingCategory.SETTLEMENT_TAB,
    TrackingAction.STATEMENT_DOWNLOADED,
    {
      pageType: 'settlement-tab',
      secondsSinceGeneration: generatedAt
        ? Math.round((Date.now() - new Date(generatedAt).getTime()) / 1000)
        : null,
    },
  );
}

async function handleTrustStatementDownload() {
  if (trustStatementDownloading.value) return;
  trustStatementDownloading.value = true;
  try {
    const blob = await settlementStore.downloadTrustStatementBlob();
    if (!blob) return;

    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'trust-statement.pdf';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  } catch {
    // Error toast handled in store action
  } finally {
    trustStatementDownloading.value = false;
  }
}

// ── Post-Settlement Action Handlers (V2-FE-1) ────────────

const postSettlementDialogConfigs: Record<
  PostSettlementActionKey,
  { title: string; message: string; confirmText: string }
> = {
  undoInvoice: {
    title: 'Undo Invoice?',
    message:
      'This will undo the settlement invoice. All fees, expenses, and time entries will return to Ready to Bill. Continue?',
    confirmText: 'Undo Invoice',
  },
  undoTrust: {
    title: 'Undo Trust Payment?',
    message:
      'This will reverse the trust payment for this invoice. The trust balance will be restored. Continue?',
    confirmText: 'Undo Trust Payment',
  },
  undoQbo: {
    title: 'Undo QuickBooks Disbursement?',
    message:
      'This will delete the client disbursement entry from QuickBooks. Continue?',
    confirmText: 'Undo QuickBooks Entry',
  },
};

async function handleFinalizeInvoice() {
  postSettlementColumnError.value = null;
  postSavingColumn.value = 'invoice';
  try {
    await settlementStore.finalizeInvoice();
    handleSuccess('Invoice finalized successfully');
  } catch {
    postSettlementColumnError.value = 'invoice';
  } finally {
    postSavingColumn.value = null;
  }
}

async function handlePayTrust() {
  postSettlementColumnError.value = null;
  postSavingColumn.value = 'trust';
  try {
    const apiMessage = await settlementStore.payFromTrust();
    if (apiMessage) {
      handleSuccess(apiMessage);
    }
  } catch {
    postSettlementColumnError.value = 'trust';
  } finally {
    postSavingColumn.value = null;
  }
}

async function handlePushQbo() {
  postSettlementColumnError.value = null;
  postSavingColumn.value = 'qbo';
  try {
    const apiMessage = await settlementStore.pushToQbo();
    if (apiMessage) {
      handleSuccess(apiMessage);
    }
  } catch {
    postSettlementColumnError.value = 'qbo';
  } finally {
    postSavingColumn.value = null;
  }
}

function handleViewInvoice() {
  const invoiceId = settlementStore.data?.settlementInvoiceId;
  if (!invoiceId) return;

  const domain = props.leanLawAuth?.domain || window.location.origin;
  const pdfUrl = `${domain}/v1/invoices/${invoiceId}/pdf`;
  window.open(pdfUrl, '_blank', 'noopener');
}

function showUndoDialog(action: PostSettlementActionKey) {
  const config = postSettlementDialogConfigs[action];
  postSettlementDialog.title = config.title;
  postSettlementDialog.message = config.message;
  postSettlementDialog.confirmText = config.confirmText;
  postSettlementDialog.action = action;
  postSettlementDialog.show = true;
}

async function confirmPostSettlementAction() {
  postSettlementDialog.show = false;
  postSettlementColumnError.value = null;

  const action = postSettlementDialog.action;
  if (!action) return;

  const columnMap: Record<
    PostSettlementActionKey,
    'invoice' | 'trust' | 'qbo'
  > = {
    undoInvoice: 'invoice',
    undoTrust: 'trust',
    undoQbo: 'qbo',
  };

  postSavingColumn.value = columnMap[action];
  try {
    if (action === 'undoInvoice') {
      await settlementStore.undoInvoice();
      handleSuccess('Invoice undo successful');
    } else if (action === 'undoTrust') {
      await settlementStore.undoTrustPayment();
      handleSuccess('Trust payment undo successful');
    } else if (action === 'undoQbo') {
      await settlementStore.undoQboDisbursement();
      handleSuccess('QuickBooks undo successful');
    }
  } catch {
    postSettlementColumnError.value = columnMap[action];
  } finally {
    postSavingColumn.value = null;
  }
}

// ── Update Matter Stage Handlers ─────────────────────────

function handleStageUpdateNotNow() {
  showUpdateStageDialog.value = false;
  doGenerate(); // Proceed with generation, skipping stage update
}

function handleStageUpdate() {
  showUpdateStageDialog.value = false;
  stageUpdated.value = true;
  // TODO: Call matter stage update API once endpoint is available
  doGenerate(); // Proceed with generation after marking stage updated
}

// ── Helpers ──────────────────────────────────────────────
function syncSettingsFromStore() {
  if (!settlementStore.data) return;
  settingsForm.feePercentage = settlementStore.data.feePercentage ?? null;
  settingsForm.feeMethod = settlementStore.data?.feeMethod ?? FEE_METHOD.GROSS;
  settingsForm.expenseArrangement =
    settlementStore.data?.expenseArrangement ?? EXPENSE_ARRANGEMENT.FIRM;
  settingsForm.roundToNearestDollar =
    settlementStore.data?.roundToNearestDollar ?? false;
  settingsForm.settlementDate = settlementStore.data?.settlementDate ?? null;
}

async function refreshAttorneyFeeIfAvailable() {
  if (!settlementStore.settlementId) return;
  if (!settlementStore.completedSteps.includes(2)) return;
  await settlementStore.loadAttorneyFee();
}

// ── Watchers ─────────────────────────────────────────────
watch(
  () => settlementStore.currentStep,
  (newStep, oldStep) => {
    if (!newStep) return;

    // Event 2: Wizard Step Completed — Step 5
    // The SettlementPaymentStep child is unmounted by v-if before its emit
    // propagates (the store update triggers this watcher synchronously),
    // so we track Step 5 completion here instead.
    if (
      oldStep === 5 &&
      newStep > 5 &&
      settlementStore.completedSteps.includes(5)
    ) {
      trackSettlement(
        TrackingCategory.SETTLEMENT_WIZARD,
        TrackingAction.WIZARD_STEP_COMPLETED,
        {
          pageType: 'settlement-wizard',
          stepNumber: 5,
          stepName: 'Map Payments',
          totalStepsAvailable: isMapPaymentsDisabled.value ? 5 : 6,
          durationSeconds: stepDurationSeconds(5),
          feeMethod: settingsForm.feeMethod || 'not_configured',
          expenseArrangement:
            settingsForm.expenseArrangement || 'not_configured',
        },
      );
    }

    // Skip Step 5 when payments mapping is not applicable
    if (newStep === 5 && isMapPaymentsDisabled.value) {
      activeStep.value = 6;
    } else {
      activeStep.value = newStep;
    }
  },
);

// Clear Step 1 validation error when selection changes
watch(
  () => settlementStore.deposits.selectedIds.length,
  () => {
    step1ValidationError.value = '';
  },
);

// Event 9: Wizard Step Viewed. activeStep is ref(1) and never *changes* to 1, so the
// entry view is signalled by a load finishing, not by a step change. onActivated reloads
// on tab re-entry, which re-fires for the step on screen — a re-entry is a genuine view.
watch(
  [() => activeStep.value, () => uiState.loading],
  ([step, loading]) => {
    // loadSucceeded, not just !loading: a bail-out or a failed load clears loading too,
    // and counting those would inflate the very denominator this event exists to supply.
    if (!step || loading || !uiState.loadSucceeded) return;

    const stepDef = stepDefinitions.find((s) => s.number === step);
    trackSettlement(
      TrackingCategory.SETTLEMENT_WIZARD,
      TrackingAction.WIZARD_STEP_VIEWED,
      {
        pageType: 'settlement-wizard',
        stepNumber: step,
        stepName: stepDef?.label ?? '',
        isRevisit: allCompletedSteps.value.includes(step),
      },
    );
    stepViewedAt.set(step, Date.now());
  },
  { immediate: true },
);

// Trigger preview PDF generation when arriving at Step 6 (pre-generation only).
// Deliberately still keyed on the step alone: adding loading as a source would also
// kick off a generate when a load finishes with step 6 already on screen.
watch(
  () => activeStep.value,
  (step) => {
    if (
      step === 6 &&
      !showNarrativeEditor.value &&
      !settlementStore.isGenerated &&
      settlementStore.canGenerateStatement &&
      !settlementStore.statement.previewBlobUrl &&
      !settlementStore.statement.previewGenerating &&
      !settlementStore.statement.previewPolling
    ) {
      settlementStore.generatePreview();
    }
  },
);
</script>

<style>
/*
 * Plain CSS (not Tailwind) for three reasons:
 *  1. Vuetify deep selectors — TW classes can't reach generated component internals.
 *  2. --sw-* CSS custom properties shared across child selectors — no TW equivalent yet (TW v4 @theme will fix this).
 *  3. Responsive grid-column on siblings — parent-breakpoint-aware layout can't be expressed with element-scoped utilities.
 */

/* Stats bar: icon inherits gray by default, transitions to LeanLaw green on hover */
.stat-chip .v-icon {
  color: #9ca3af;
  transition: color 0.15s ease;
}
.stat-chip:hover .v-icon {
  color: #29bf12 !important;
}

/* ── Legacy horizontal stepper (settlementRedesign OFF) ──────────────
 * Restores the pre-SC-45776 stepper appearance.
 * Cleanup: delete this block when the settlementRedesign flipper is removed.
 * ─────────────────────────────────────────────────────────────────── */
/* Circles only — remove gray tile backgrounds */
.settlement-stepper .v-stepper-item {
  background: transparent !important;
}
.settlement-stepper .v-stepper-item--selected {
  background: transparent !important;
}
/* Remove bottom border/shadow from stepper header */
.settlement-stepper .v-stepper-header {
  box-shadow: none !important;
  border-bottom: none !important;
}
/* Green title text for completed and active steps */
.settlement-stepper .v-stepper-item--complete .v-stepper-item__title,
.settlement-stepper .v-stepper-item--selected .v-stepper-item__title {
  color: #4caf50 !important;
}
.settlement-stepper .v-stepper-item--complete .v-stepper-item__subtitle,
.settlement-stepper .v-stepper-item--selected .v-stepper-item__subtitle {
  color: #4caf50 !important;
}
/* Connecting lines between step circles */
.settlement-stepper .v-stepper-header .v-divider {
  align-self: flex-start !important;
  margin: 35px -67px 0 !important;
  border-top-width: 2px !important;
  border-top-style: solid !important;
  opacity: 1 !important;
}
.settlement-stepper .v-stepper-header .v-divider.step-line-default {
  border-color: #bdbdbd !important;
}
.settlement-stepper .v-stepper-header .v-divider.step-line-green {
  border-color: #4caf50 !important;
  border-top-width: 3px !important;
}

/* ── SC-45776: Design tokens ──────────────────────────────────────────
 * All colours live here as CSS custom properties scoped to the wizard
 * grid, so they don't pollute the global stylesheet.
 * Breakpoints account for the ~350px Angular sidebar:
 *   < 768px    → single-column stack
 *   768–1279px → rail + content (summary full-width below)
 *   1280px+    → full 3-column (220px | 1fr | 280px)
 * ─────────────────────────────────────────────────────────────────── */
.sett-wizard-grid {
  --sw-green:        #29bf12;
  --sw-green-dark:   #1b5e20;
  --sw-green-light:  #f1faf4;
  --sw-green-border: #bfe0c6;
  --sw-border:       #e8eaed;
  --sw-border-inner: #eef0f2;
  --sw-surface:      #f6f8fa;
  --sw-text:         #2b3038;
  --sw-text-body:    #3a4250;
  --sw-text-muted:   #8a929c;
  --sw-shadow:       0 1px 2px rgba(16,24,40,.04), 0 10px 28px rgba(16,24,40,.05);

  display: grid;
  grid-template-columns: 1fr;
  gap: 16px;
}

/* ── Breakpoints (all 768px rules in one block, all 1280px in one block) */
@media (min-width: 768px) {
  .sett-wizard-grid {
    grid-template-columns: 200px minmax(0, 1fr);
    gap: 20px;
  }
  /* Summary card spans full width below rail+content at mid viewport */
  .sett-summary-card { grid-column: 1 / -1; }
  /* Rail sticky only when it's a side column */
  .sett-rail { position: sticky; top: 16px; }
}

@media (min-width: 1280px) {
  .sett-wizard-grid {
    grid-template-columns: 220px minmax(0, 1fr) 280px;
    gap: 24px;
  }
  /* Summary card becomes its own column */
  .sett-summary-card { grid-column: auto; position: sticky; top: 16px; align-self: start; }
}

/* ── Rail ─────────────────────────────────────────────────────────── */
.sett-rail {
  background: #fff;
  border: 1px solid var(--sw-border);
  border-radius: 16px;
  padding: 16px 14px;
  box-shadow: var(--sw-shadow);
  align-self: start;
}
.sett-rail-head { padding: 2px 4px 14px; }
.sett-rail-title {
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.07em;
  text-transform: uppercase;
  color: var(--sw-text-muted);
  margin-bottom: 10px;
}
.sett-rail-progress {
  height: 5px;
  background: var(--sw-border-inner);
  border-radius: 999px;
  overflow: hidden;
}
.sett-rail-progress-bar {
  height: 100%;
  background: linear-gradient(90deg, var(--sw-green), #46d62f);
  border-radius: 999px;
  transition: width 0.4s cubic-bezier(0.23, 1, 0.32, 1);
}
.sett-rail-count {
  font-size: 11px;
  font-weight: 600;
  color: var(--sw-text-muted);
  margin-top: 8px;
  letter-spacing: 0.01em;
}
.sett-rail-locked-badge {
  display: inline-flex;
  align-items: center;
  margin-top: 8px;
  padding: 3px 8px;
  border-radius: 999px;
  background: var(--sw-green-light);
  border: 1px solid var(--sw-green-border);
  color: var(--sw-green-dark);
  font-size: 10.5px;
  font-weight: 600;
  letter-spacing: 0.01em;
}

/* ── Step items ───────────────────────────────────────────────────── */
.sett-step {
  position: relative;
  display: flex;
  align-items: center;
  gap: 11px;
  padding: 9px 10px;
  border-radius: 12px;
  border: 1px solid transparent;
  transition: background 0.15s, box-shadow 0.15s;
}
.sett-step--clickable { cursor: pointer; }
.sett-step--clickable:hover { background: var(--sw-surface); }
.sett-step--active {
  background: var(--sw-green-light);
  border-color: var(--sw-green-border);
  box-shadow: 0 1px 2px rgba(41,191,18,.1);
}
.sett-step--active::before {
  content: '';
  position: absolute;
  left: 0; top: 9px; bottom: 9px;
  width: 3px;
  border-radius: 999px;
  background: var(--sw-green);
}

/* Step circle */
.sett-step-circle {
  width: 27px;
  height: 27px;
  border-radius: 50%;
  display: grid;
  place-items: center;
  font-size: 12px;
  font-weight: 700;
  background: var(--sw-border-inner);
  color: var(--sw-text-muted);
  flex-shrink: 0;
  transition: background 0.15s, box-shadow 0.15s, color 0.15s;
}
.sett-step--active .sett-step-circle {
  background: #fff;
  color: var(--sw-green-dark);
  box-shadow: 0 0 0 2px var(--sw-green) inset;
}
.sett-step--completed .sett-step-circle { background: var(--sw-green); color: #fff; }

/* Step label */
.sett-step-label { font-size: 13px; font-weight: 500; color: var(--sw-text-body); line-height: 1.3; }
.sett-step--active .sett-step-label { color: var(--sw-green-dark); font-weight: 600; }
.sett-step--completed .sett-step-label { color: var(--sw-text); }

/* Connector line between steps */
.sett-step-line { width: 2px; height: 14px; background: #e3e5e8; margin-left: 22px; }
.sett-step-line--done { background: var(--sw-green); }

/* Rail footer */
.sett-rail-foot {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 14px;
  padding-top: 14px;
  border-top: 1px solid var(--sw-border-inner);
}
.sett-rail-btn {
  flex: 1 1 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 5px;
  border: 1px solid #d6dbe1;
  background: #fff;
  color: var(--sw-text-body);
  font-family: inherit;
  font-size: 12.5px;
  font-weight: 600;
  padding: 8px 10px;
  border-radius: 10px;
  cursor: pointer;
  transition: background 0.15s, border-color 0.15s, color 0.15s;
  white-space: nowrap;
}
.sett-rail-btn:hover:not(:disabled) { background: var(--sw-surface); border-color: #bcc3cc; color: var(--sw-text); }
.sett-rail-btn:disabled { opacity: 0.4; cursor: not-allowed; }
.sett-rail-btn--unlock:hover:not(:disabled) {
  background: var(--sw-green-light);
  border-color: var(--sw-green);
  color: var(--sw-green-dark);
}

/* ── Step content panel ───────────────────────────────────────────── */
/* overflow:hidden intentionally omitted — would clip dropdowns/date-pickers */
.sett-content-panel {
  min-width: 0;
  align-self: start;
  background: #fff;
  border: 1px solid var(--sw-border);
  border-radius: 12px;
  box-shadow: 0 1px 2px rgba(16,24,40,.04), 0 2px 8px rgba(16,24,40,.06);
}
.sett-step-body { min-width: 0; }

/* Each step component's root <v-card> fills the panel edge-to-edge with its own
 * (unrounded) corners. Since the panel above has no overflow:hidden, that square
 * corner just sits in the notch the panel's radius cuts away — the panel's rounding
 * never gets a chance to show. Matching the radius here fixes it without clipping. */
.sett-step-body > .v-card {
  border-radius: 12px;
}

/* Allow wide tables to scroll without clipping absolute-positioned pickers */
.sett-step-body .v-table__wrapper,
.sett-step-body .overflow-x-auto { overflow-x: auto; }

/* ── Step footer ──────────────────────────────────────────────────────
 * Each step component's nav button row carries .sett-step-footer.
 * Using a semantic class avoids selecting Tailwind utilities and
 * removes the need for !important overrides.
 * ─────────────────────────────────────────────────────────────────── */
.sett-step-body .sett-step-footer {
  display: flex;
  justify-content: space-between;
  margin-top: 0;
  padding: 14px 20px;
  border-top: 1px solid var(--sw-border-inner);
  gap: 0;
  padding-left: 0;
  padding-right: 0;
}

/* ── Summary card ─────────────────────────────────────────────────── */
/* .sett-summary-card is added directly to SettlementSummaryCard's root
 * <v-card> — no compound Vuetify+Tailwind selectors needed. */
.sett-summary-card {
  border: 1px solid var(--sw-border) !important;
  border-radius: 16px !important;
  box-shadow: var(--sw-shadow) !important;
}
.sett-summary-card .v-card-title {
  border-bottom-color: var(--sw-border-inner) !important;
  font-size: 13px !important;
  font-weight: 700 !important;
  color: var(--sw-text) !important;
}

/* ── Step-change fade transition ──────────────────────────────────── */
.sett-step-fade-enter-active,
.sett-step-fade-leave-active { transition: opacity 0.18s ease, transform 0.18s ease; }
.sett-step-fade-enter-from { opacity: 0; transform: translateY(6px); }
.sett-step-fade-leave-to   { opacity: 0; transform: translateY(-6px); }
</style>
